window.theme = window.theme || {};


/**
 * Image Helper Functions
 * -----------------------------------------------------------------------------
 * A collection of functions that help with basic image operations.
 *
 */

theme.Images = (function() {
    /**
     * Preloads an image in memory and uses the browsers cache to store it until needed.
     *
     * @param {Array} images - A list of image urls
     * @param {String} size - A shopify image size attribute
     */

    function preload(images, size) {
        if (typeof images === 'string') {
            images = [images];
        }

        for (var i = 0; i < images.length; i++) {
            var image = images[i];
            this.loadImage(this.getSizedImageUrl(image, size));
        }
    }

    /**
     * Loads and caches an image in the browsers cache.
     * @param {string} path - An image url
     */
    function loadImage(path) {
        new Image().src = path;
    }

    /**
     * Swaps the src of an image for another OR returns the imageURL to the callback function
     * @param image
     * @param element
     * @param callback
     */
    function switchImage(image, element, callback) {
        var size = this.imageSize(element.src);
        var imageUrl = this.getSizedImageUrl(image.src, size);

        if (callback) {
            callback(imageUrl, image, element); // eslint-disable-line callback-return
        } else {
            element.src = imageUrl;
        }
    }

    /**
     * +++ Useful
     * Find the Shopify image attribute size
     *
     * @param {string} src
     * @returns {null}
     */
    function imageSize(src) {
        var match = src.match(
          /.+_((?:pico|icon|thumb|small|compact|medium|large|grande)|\d{1,4}x\d{0,4}|x\d{1,4})[_\\.@]/
        );

        if (match !== null) {
            if (match[2] !== undefined) {
                return match[1] + match[2];
            } else {
                return match[1];
            }
        } else {
            return null;
        }
    }

    /**
     * +++ Useful
     * Adds a Shopify size attribute to a URL
     *
     * @param src
     * @param size
     * @returns {*}
     */
    function getSizedImageUrl(src, size) {
        if (size === null) {
            return src;
        }

        if (size === 'master') {
            return this.removeProtocol(src);
        }

        var match = src.match(
          /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i
        );

        if (match !== null) {
            var prefix = src.split(match[0]);
            var suffix = match[0];

            return this.removeProtocol(prefix[0] + '_' + size + suffix);
        }

        return null;
    }

    function removeProtocol(path) {
        return path.replace(/http(s)?:/, '');
    }

    return {
        preload: preload,
        loadImage: loadImage,
        switchImage: switchImage,
        imageSize: imageSize,
        getSizedImageUrl: getSizedImageUrl,
        removeProtocol: removeProtocol
    };
})();

/**
 * Currency Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help with currency formatting
 *
 * Current contents
 * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
 *
 * Alternatives
 * - Accounting.js - http://openexchangerates.github.io/accounting.js/
 *
 */

theme.Currency = (function() {
    var moneyFormat = '${{amount}}'; // eslint-disable-line camelcase

    function formatMoney(cents, format) {
        if (typeof cents === 'string') {
            cents = cents.replace('.', '');
        }
        var value = '';
        var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        var formatString = format || moneyFormat;

        function formatWithDelimiters(number, precision, thousands, decimal) {
            thousands = thousands || ',';
            decimal = decimal || '.';

            if (isNaN(number) || number === null) {
                return 0;
            }

            number = (number / 100.0).toFixed(precision);

            var parts = number.split('.');
            var dollarsAmount = parts[0].replace(
              /(\d)(?=(\d\d\d)+(?!\d))/g,
              '$1' + thousands
            );
            var centsAmount = parts[1] ? decimal + parts[1] : '';

            return dollarsAmount + centsAmount;
        }

        switch (formatString.match(placeholderRegex)[1]) {
            case 'amount':
                value = formatWithDelimiters(cents, 2);
                break;
            case 'amount_no_decimals':
                value = formatWithDelimiters(cents, 0);
                break;
            case 'amount_with_comma_separator':
                value = formatWithDelimiters(cents, 2, '.', ',');
                break;
            case 'amount_no_decimals_with_comma_separator':
                value = formatWithDelimiters(cents, 0, '.', ',');
                break;
            case 'amount_no_decimals_with_space_separator':
                value = formatWithDelimiters(cents, 0, ' ');
                break;
            case 'amount_with_apostrophe_separator':
                value = formatWithDelimiters(cents, 2, "'");
                break;
        }

        return formatString.replace(placeholderRegex, value);
    }

    return {
        formatMoney: formatMoney
    };
})();

/**
 * Variant Selection scripts
 * ------------------------------------------------------------------------------
 *
 * Handles change events from the variant inputs in any `cart/add` forms that may
 * exist.  Also updates the master select and triggers updates when the variants
 * price or image changes.
 *
 * @namespace variants
 */

slate.Variants = (function() {
    /**
     * Variant constructor
     *
     * @param {object} options - Settings from `product.js`
     */
    function Variants(options) {
        this.container = options.container;
        this.product = options.product;
        this.originalSelectorId = options.originalSelectorId;
        this.enableHistoryState = options.enableHistoryState;
        this.singleOptions = this.container.querySelectorAll(
          options.singleOptionSelector
        );
        this.currentVariant = this._getVariantFromOptions();

        this.singleOptions.forEach(
          function(option) {
              option.addEventListener('change', this._onSelectChange.bind(this));
          }.bind(this)
        );

        this.singleOptionsTriger = this.container.querySelectorAll(
            options.singleOptionInput
        );
        if(this.singleOptionsTriger) {
            this.singleOptionsTriger.forEach(function(input) {
                    input.addEventListener('click', this._inputChange.bind(this));
                }.bind(this)
            );
        }
    }

    Variants.prototype = Object.assign({}, Variants.prototype, {
        /**
         * Get the currently selected options from add-to-cart form. Works with all
         * form input elements.
         *
         * @return {array} options - Values of currently selected variants
         */
        _getCurrentOptions: function() {
            var result = [];

            this.singleOptions.forEach(function(option) {
                var type = option.getAttribute('type');
                var isRadioOrCheckbox = type === 'radio' || type === 'checkbox';

                if (!isRadioOrCheckbox || option.checked) {
                    result.push({
                        value: option.value,
                        index: option.getAttribute('data-index')
                    });
                }
            });

            return result;
        },

        /**
         * Find variant based on selected values.
         *
         * @param  {array} selectedValues - Values of variant inputs
         * @return {object || undefined} found - Variant object from product.variants
         */
        _getVariantFromOptions: function() {
            var selectedValues = this._getCurrentOptions();
            var variants = this.product.variants;

            var found = variants.find(function(variant) {
                return selectedValues.every(function(values) {
                    return variant[values.index] === values.value;
                });
            });

            return found;
        },

        /**
         * Event handler for when a variant input changes.
         */
        _onSelectChange: function() {
            var variant = this._getVariantFromOptions();

            this.container.dispatchEvent(
              new CustomEvent('variantChange', {
                  detail: {
                      variant: variant
                  },
                  bubbles: true,
                  cancelable: true
              })
            );

            if (!variant) {
                return;
            }

            this._updateMasterSelect(variant);
            this._updateImages(variant);
            this._updatePrice(variant);
            //  this._updateSKU(variant);
            this.currentVariant = variant;

            if (this.enableHistoryState) {
                this._updateHistoryState(variant);
            }

        },

        _getCurrentOptionsInput: function() {
            var result = [];
            this.singleOptionsTriger.forEach(function(option) {
                if (option.checked) {
                    result.push({
                        value: option.value,
                        index: option.getAttribute('data-index')
                    });
                }
            });
            return result;
        },

        _getVariantFromInput: function() {
            var selectedValues = this._getCurrentOptionsInput();
            var variants = this.product.variants;

            var found = variants.find(function(variant) {
                return selectedValues.every(function(values) {
                    return variant[values.index] === values.value;
                });
            });

            return found;
        },

        /**
         * Event handler for when a variant input changes.
         */

        _inputChange: function () {

            var variant = this._getVariantFromInput();

            this.container.dispatchEvent(
                new CustomEvent('variantChange', {
                    detail: {
                        variant: variant
                    },
                    bubbles: true,
                    cancelable: true
                })
            );

            if (!variant) {
                return;
            }

            this._updateMasterSelect(variant);
          //  this._updateImages(variant);
            this._updatePrice(variant);
            this._updateSKU(variant);
            this.currentVariant = variant;

            if (this.enableHistoryState) {
                this._updateHistoryState(variant);
            }
        },

        /**
         * Trigger event when variant image changes
         *
         * @param  {object} variant - Currently selected variant
         * @return {event}  variantImageChange
         */
        _updateImages: function(variant) {
            var variantImage = variant.featured_image || {};
            var currentVariantImage = this.currentVariant.featured_image || {};

            if (
              !variant.featured_image ||
              variantImage.src === currentVariantImage.src
            ) {
                return;
            }

            this.container.dispatchEvent(
              new CustomEvent('variantImageChange', {
                  detail: {
                      variant: variant
                  },
                  bubbles: true,
                  cancelable: true
              })
            );
        },

        /**
         * Trigger event when variant price changes.
         *
         * @param  {object} variant - Currently selected variant
         * @return {event} variantPriceChange
         */
        _updatePrice: function(variant) {
            if (
              variant.price === this.currentVariant.price &&
              variant.compare_at_price === this.currentVariant.compare_at_price &&
              variant.unit_price === this.currentVariant.unit_price
            ) {
                return;
            }

            this.container.dispatchEvent(
              new CustomEvent('variantPriceChange', {
                  detail: {
                      variant: variant
                  },
                  bubbles: true,
                  cancelable: true
              })
            );
        },

        /**
         * Trigger event when variant sku changes.
         *
         * @param  {object} variant - Currently selected variant
         * @return {event} variantSKUChange
         */
        _updateSKU: function(variant) {
            if (variant.sku === this.currentVariant.sku) {
                return;
            }

            this.container.dispatchEvent(
              new CustomEvent('variantSKUChange', {
                  detail: {
                      variant: variant
                  },
                  bubbles: true,
                  cancelable: true
              })
            );
        },

        /**
         * Update history state for product deeplinking
         *
         * @param  {variant} variant - Currently selected variant
         * @return {k}         [description]
         */
        _updateHistoryState: function(variant) {
            if (!history.replaceState || !variant) {
                return;
            }

            var newurl =
              window.location.protocol +
              '//' +
              window.location.host +
              window.location.pathname +
              '?variant=' +
              variant.id;
            window.history.replaceState({ path: newurl }, '', newurl);
        },

        /**
         * Update hidden master select of variant change
         *
         * @param  {variant} variant - Currently selected variant
         */
        _updateMasterSelect: function(variant) {
            var masterSelect = this.container.querySelector(this.originalSelectorId);

            if (!masterSelect) return;
            masterSelect.value = variant.id;
        }
    });

    return Variants;
})();

// adddddddd

/**
 * Utility helpers
 * -----------------------------------------------------------------------------
 * A collection of useful global functions
 *
 */

/**
 * Initializes the handleize function on the String object.
 * Based on {@link https://github.com/nicbou/stringops stringops} by Nicolas Bouliane.
 *
 * @private
 * @memberof Utilities
 */
function _handleize() {
    // UTF to ASCII map. Removes accents.
    var defaultDiacriticsRemovalap = [
        {'base':'A', 'letters':'\u0041\u24B6\uFF21\u00C0\u00C1\u00C2\u1EA6\u1EA4\u1EAA\u1EA8\u00C3\u0100\u0102\u1EB0\u1EAE\u1EB4\u1EB2\u0226\u01E0\u00C4\u01DE\u1EA2\u00C5\u01FA\u01CD\u0200\u0202\u1EA0\u1EAC\u1EB6\u1E00\u0104\u023A\u2C6F'},
        {'base':'AA','letters':'\uA732'},
        {'base':'AE','letters':'\u00C6\u01FC\u01E2'},
        {'base':'AO','letters':'\uA734'},
        {'base':'AU','letters':'\uA736'},
        {'base':'AV','letters':'\uA738\uA73A'},
        {'base':'AY','letters':'\uA73C'},
        {'base':'B', 'letters':'\u0042\u24B7\uFF22\u1E02\u1E04\u1E06\u0243\u0182\u0181'},
        {'base':'C', 'letters':'\u0043\u24B8\uFF23\u0106\u0108\u010A\u010C\u00C7\u1E08\u0187\u023B\uA73E'},
        {'base':'D', 'letters':'\u0044\u24B9\uFF24\u1E0A\u010E\u1E0C\u1E10\u1E12\u1E0E\u0110\u018B\u018A\u0189\uA779'},
        {'base':'DZ','letters':'\u01F1\u01C4'},
        {'base':'Dz','letters':'\u01F2\u01C5'},
        {'base':'E', 'letters':'\u0045\u24BA\uFF25\u00C8\u00C9\u00CA\u1EC0\u1EBE\u1EC4\u1EC2\u1EBC\u0112\u1E14\u1E16\u0114\u0116\u00CB\u1EBA\u011A\u0204\u0206\u1EB8\u1EC6\u0228\u1E1C\u0118\u1E18\u1E1A\u0190\u018E'},
        {'base':'F', 'letters':'\u0046\u24BB\uFF26\u1E1E\u0191\uA77B'},
        {'base':'G', 'letters':'\u0047\u24BC\uFF27\u01F4\u011C\u1E20\u011E\u0120\u01E6\u0122\u01E4\u0193\uA7A0\uA77D\uA77E'},
        {'base':'H', 'letters':'\u0048\u24BD\uFF28\u0124\u1E22\u1E26\u021E\u1E24\u1E28\u1E2A\u0126\u2C67\u2C75\uA78D'},
        {'base':'I', 'letters':'\u0049\u24BE\uFF29\u00CC\u00CD\u00CE\u0128\u012A\u012C\u0130\u00CF\u1E2E\u1EC8\u01CF\u0208\u020A\u1ECA\u012E\u1E2C\u0197'},
        {'base':'J', 'letters':'\u004A\u24BF\uFF2A\u0134\u0248'},
        {'base':'K', 'letters':'\u004B\u24C0\uFF2B\u1E30\u01E8\u1E32\u0136\u1E34\u0198\u2C69\uA740\uA742\uA744\uA7A2'},
        {'base':'L', 'letters':'\u004C\u24C1\uFF2C\u013F\u0139\u013D\u1E36\u1E38\u013B\u1E3C\u1E3A\u0141\u023D\u2C62\u2C60\uA748\uA746\uA780'},
        {'base':'LJ','letters':'\u01C7'},
        {'base':'Lj','letters':'\u01C8'},
        {'base':'M', 'letters':'\u004D\u24C2\uFF2D\u1E3E\u1E40\u1E42\u2C6E\u019C'},
        {'base':'N', 'letters':'\u004E\u24C3\uFF2E\u01F8\u0143\u00D1\u1E44\u0147\u1E46\u0145\u1E4A\u1E48\u0220\u019D\uA790\uA7A4'},
        {'base':'NJ','letters':'\u01CA'},
        {'base':'Nj','letters':'\u01CB'},
        {'base':'O', 'letters':'\u004F\u24C4\uFF2F\u00D2\u00D3\u00D4\u1ED2\u1ED0\u1ED6\u1ED4\u00D5\u1E4C\u022C\u1E4E\u014C\u1E50\u1E52\u014E\u022E\u0230\u00D6\u022A\u1ECE\u0150\u01D1\u020C\u020E\u01A0\u1EDC\u1EDA\u1EE0\u1EDE\u1EE2\u1ECC\u1ED8\u01EA\u01EC\u00D8\u01FE\u0186\u019F\uA74A\uA74C'},
        {'base':'OI','letters':'\u01A2'},
        {'base':'OO','letters':'\uA74E'},
        {'base':'OU','letters':'\u0222'},
        {'base':'OE','letters':'\u008C\u0152'},
        {'base':'oe','letters':'\u009C\u0153'},
        {'base':'P', 'letters':'\u0050\u24C5\uFF30\u1E54\u1E56\u01A4\u2C63\uA750\uA752\uA754'},
        {'base':'Q', 'letters':'\u0051\u24C6\uFF31\uA756\uA758\u024A'},
        {'base':'R', 'letters':'\u0052\u24C7\uFF32\u0154\u1E58\u0158\u0210\u0212\u1E5A\u1E5C\u0156\u1E5E\u024C\u2C64\uA75A\uA7A6\uA782'},
        {'base':'S', 'letters':'\u0053\u24C8\uFF33\u1E9E\u015A\u1E64\u015C\u1E60\u0160\u1E66\u1E62\u1E68\u0218\u015E\u2C7E\uA7A8\uA784'},
        {'base':'T', 'letters':'\u0054\u24C9\uFF34\u1E6A\u0164\u1E6C\u021A\u0162\u1E70\u1E6E\u0166\u01AC\u01AE\u023E\uA786'},
        {'base':'TZ','letters':'\uA728'},
        {'base':'U', 'letters':'\u0055\u24CA\uFF35\u00D9\u00DA\u00DB\u0168\u1E78\u016A\u1E7A\u016C\u00DC\u01DB\u01D7\u01D5\u01D9\u1EE6\u016E\u0170\u01D3\u0214\u0216\u01AF\u1EEA\u1EE8\u1EEE\u1EEC\u1EF0\u1EE4\u1E72\u0172\u1E76\u1E74\u0244'},
        {'base':'V', 'letters':'\u0056\u24CB\uFF36\u1E7C\u1E7E\u01B2\uA75E\u0245'},
        {'base':'VY','letters':'\uA760'},
        {'base':'W', 'letters':'\u0057\u24CC\uFF37\u1E80\u1E82\u0174\u1E86\u1E84\u1E88\u2C72'},
        {'base':'X', 'letters':'\u0058\u24CD\uFF38\u1E8A\u1E8C'},
        {'base':'Y', 'letters':'\u0059\u24CE\uFF39\u1EF2\u00DD\u0176\u1EF8\u0232\u1E8E\u0178\u1EF6\u1EF4\u01B3\u024E\u1EFE'},
        {'base':'Z', 'letters':'\u005A\u24CF\uFF3A\u0179\u1E90\u017B\u017D\u1E92\u1E94\u01B5\u0224\u2C7F\u2C6B\uA762'},
        {'base':'a', 'letters':'\u0061\u24D0\uFF41\u1E9A\u00E0\u00E1\u00E2\u1EA7\u1EA5\u1EAB\u1EA9\u00E3\u0101\u0103\u1EB1\u1EAF\u1EB5\u1EB3\u0227\u01E1\u00E4\u01DF\u1EA3\u00E5\u01FB\u01CE\u0201\u0203\u1EA1\u1EAD\u1EB7\u1E01\u0105\u2C65\u0250'},
        {'base':'aa','letters':'\uA733'},
        {'base':'ae','letters':'\u00E6\u01FD\u01E3'},
        {'base':'ao','letters':'\uA735'},
        {'base':'au','letters':'\uA737'},
        {'base':'av','letters':'\uA739\uA73B'},
        {'base':'ay','letters':'\uA73D'},
        {'base':'b', 'letters':'\u0062\u24D1\uFF42\u1E03\u1E05\u1E07\u0180\u0183\u0253'},
        {'base':'c', 'letters':'\u0063\u24D2\uFF43\u0107\u0109\u010B\u010D\u00E7\u1E09\u0188\u023C\uA73F\u2184'},
        {'base':'d', 'letters':'\u0064\u24D3\uFF44\u1E0B\u010F\u1E0D\u1E11\u1E13\u1E0F\u0111\u018C\u0256\u0257\uA77A'},
        {'base':'dz','letters':'\u01F3\u01C6'},
        {'base':'e', 'letters':'\u0065\u24D4\uFF45\u00E8\u00E9\u00EA\u1EC1\u1EBF\u1EC5\u1EC3\u1EBD\u0113\u1E15\u1E17\u0115\u0117\u00EB\u1EBB\u011B\u0205\u0207\u1EB9\u1EC7\u0229\u1E1D\u0119\u1E19\u1E1B\u0247\u025B\u01DD'},
        {'base':'f', 'letters':'\u0066\u24D5\uFF46\u1E1F\u0192\uA77C'},
        {'base':'g', 'letters':'\u0067\u24D6\uFF47\u01F5\u011D\u1E21\u011F\u0121\u01E7\u0123\u01E5\u0260\uA7A1\u1D79\uA77F'},
        {'base':'h', 'letters':'\u0068\u24D7\uFF48\u0125\u1E23\u1E27\u021F\u1E25\u1E29\u1E2B\u1E96\u0127\u2C68\u2C76\u0265'},
        {'base':'hv','letters':'\u0195'},
        {'base':'i', 'letters':'\u0069\u24D8\uFF49\u00EC\u00ED\u00EE\u0129\u012B\u012D\u00EF\u1E2F\u1EC9\u01D0\u0209\u020B\u1ECB\u012F\u1E2D\u0268\u0131'},
        {'base':'j', 'letters':'\u006A\u24D9\uFF4A\u0135\u01F0\u0249'},
        {'base':'k', 'letters':'\u006B\u24DA\uFF4B\u1E31\u01E9\u1E33\u0137\u1E35\u0199\u2C6A\uA741\uA743\uA745\uA7A3'},
        {'base':'l', 'letters':'\u006C\u24DB\uFF4C\u0140\u013A\u013E\u1E37\u1E39\u013C\u1E3D\u1E3B\u017F\u0142\u019A\u026B\u2C61\uA749\uA781\uA747'},
        {'base':'lj','letters':'\u01C9'},
        {'base':'m', 'letters':'\u006D\u24DC\uFF4D\u1E3F\u1E41\u1E43\u0271\u026F'},
        {'base':'n', 'letters':'\u006E\u24DD\uFF4E\u01F9\u0144\u00F1\u1E45\u0148\u1E47\u0146\u1E4B\u1E49\u019E\u0272\u0149\uA791\uA7A5'},
        {'base':'nj','letters':'\u01CC'},
        {'base':'o', 'letters':'\u006F\u24DE\uFF4F\u00F2\u00F3\u00F4\u1ED3\u1ED1\u1ED7\u1ED5\u00F5\u1E4D\u022D\u1E4F\u014D\u1E51\u1E53\u014F\u022F\u0231\u00F6\u022B\u1ECF\u0151\u01D2\u020D\u020F\u01A1\u1EDD\u1EDB\u1EE1\u1EDF\u1EE3\u1ECD\u1ED9\u01EB\u01ED\u00F8\u01FF\u0254\uA74B\uA74D\u0275'},
        {'base':'oi','letters':'\u01A3'},
        {'base':'ou','letters':'\u0223'},
        {'base':'oo','letters':'\uA74F'},
        {'base':'p','letters':'\u0070\u24DF\uFF50\u1E55\u1E57\u01A5\u1D7D\uA751\uA753\uA755'},
        {'base':'q','letters':'\u0071\u24E0\uFF51\u024B\uA757\uA759'},
        {'base':'r','letters':'\u0072\u24E1\uFF52\u0155\u1E59\u0159\u0211\u0213\u1E5B\u1E5D\u0157\u1E5F\u024D\u027D\uA75B\uA7A7\uA783'},
        {'base':'s','letters':'\u0073\u24E2\uFF53\u00DF\u015B\u1E65\u015D\u1E61\u0161\u1E67\u1E63\u1E69\u0219\u015F\u023F\uA7A9\uA785\u1E9B'},
        {'base':'t','letters':'\u0074\u24E3\uFF54\u1E6B\u1E97\u0165\u1E6D\u021B\u0163\u1E71\u1E6F\u0167\u01AD\u0288\u2C66\uA787'},
        {'base':'tz','letters':'\uA729'},
        {'base':'u','letters': '\u0075\u24E4\uFF55\u00F9\u00FA\u00FB\u0169\u1E79\u016B\u1E7B\u016D\u00FC\u01DC\u01D8\u01D6\u01DA\u1EE7\u016F\u0171\u01D4\u0215\u0217\u01B0\u1EEB\u1EE9\u1EEF\u1EED\u1EF1\u1EE5\u1E73\u0173\u1E77\u1E75\u0289'},
        {'base':'v','letters':'\u0076\u24E5\uFF56\u1E7D\u1E7F\u028B\uA75F\u028C'},
        {'base':'vy','letters':'\uA761'},
        {'base':'w','letters':'\u0077\u24E6\uFF57\u1E81\u1E83\u0175\u1E87\u1E85\u1E98\u1E89\u2C73'},
        {'base':'x','letters':'\u0078\u24E7\uFF58\u1E8B\u1E8D'},
        {'base':'y','letters':'\u0079\u24E8\uFF59\u1EF3\u00FD\u0177\u1EF9\u0233\u1E8F\u00FF\u1EF7\u1E99\u1EF5\u01B4\u024F\u1EFF'},
        {'base':'z','letters':'\u007A\u24E9\uFF5A\u017A\u1E91\u017C\u017E\u1E93\u1E95\u01B6\u0225\u0240\u2C6C\uA763'}
    ];

    // Generate the diacritics map to replace accented characters.
    var diacriticsMap = {};
    for (var i = 0; i < defaultDiacriticsRemovalap.length; i++) {
        var letters = defaultDiacriticsRemovalap[i].letters.split("");

        for (var j=0; j < letters.length ; j++) {
            diacriticsMap[letters[j]] = defaultDiacriticsRemovalap[i].base;
        }
    }

    /**
     * Replaces accented characters with their non-accented counterparts.<br>
     * Based on {@link https://github.com/nicbou/stringops stringops} by Nicolas Bouliane.
     *
     * @param {boolean} [remove=false] - Whether to remove unicode characters that are not mapped to a non-accented characters.
     * 	This includes all non-ascii characters.
     * @param {string} [replacement=Empty string] - The replacement character(s) to use for invalid characters.
     * @returns {string} String without accents.
     * @memberof Utilities
     * @example
     * // returns 'My accented string$.'
     * 'My áccentéd string$.'.noAccents();
     */
    String.prototype.noAccents = function(remove, replacement) {
        // x00-x7E is the ASCII character range
        var withoutAccents = this.replace(/[^\u0000-\u007E]/g, function(ch) {
            return diacriticsMap[ch] || ch;
        });

        replacement = replacement || '';

        if (remove) {
            withoutAccents = withoutAccents.replace(/[^\u0000-\u007E]/g, replacement);
        }

        return withoutAccents;
    };

    /**
     * Returns a "handleized" version of a string suitable for URLs and file names.<br>
     * Based on {@link https://github.com/nicbou/stringops stringops} by Nicolas Bouliane.
     *
     * @param {string} [seperator=-] - The character to use as a separator.
     * @param {string} [replacement=Empty string] - The replacement character(s) to use for invalid characters.
     * @returns {string} The handleized string.
     * @memberof Utilities
     * @example
     * var myString = 'My áccentéd string$.';
     *
     * // returns 'my-accented-string'
     * myString.handleize();
     *
     * // returns 'my_accented_string##'
     * myString.handleize('_', '#');
     */
    String.prototype.handleize = function(separator, replacement) {
        var slug;
        separator = separator || '-';
        replacement = replacement || '';
        slug = this.noAccents(true, replacement).trim().replace(/[^a-z0-9\s-]/ig, replacement).replace(/[\s|-]+/ig, separator);

        return slug.toLowerCase();
    };
}

_handleize();

theme_.utils = {
    // Mimics the Liquid handleize filter in JavaScript
    // requires the stringpos.js library
    handleize: function(str) {
        return str.handleize();
    },

    /**
     * Created an object from a parameter string.
     *
     * @param {string} object_string - The string to parse to an object.
     * @returns {object} The object created from the string.
     * @memberof Utilities
     * @example
     * // If called on a form that takes a name and email, could return { name: "Joe", email: "joe@diffagency.com" }
     * theme.utils.toObject($form.serialize());
     */
    toObject: function(object_string) {
        var parameters = {};
        var elements;

        if (!object_string) {
            return {};
        }

        // Decode the query string to get original characters
        object_string = decodeURIComponent(object_string);

        // Split into each element
        elements = object_string.split('&');

        for (var i = 0; i < elements.length; i++) {
            var element = elements[i].split('=');
            parameters[element[0]] = element[1] || null;
        };

        return parameters;
    },

    /**
     * Extracts the URL parameters into an object.
     *
     * @param {string} [url=window.location.href] - The URL to parse.
     * @returns {object} The query parameters object.
     * @memberof Utilities
     * @example
     * // Returns { name: "Joe", email: "joe@diffagency.com" }
     * theme.utils.getURLParams('https://example.com/?name%3DJoe%26email%3Djoe%40diffagency.com');
     */
    getURLParams: function(url) {
        var url = url || window.location.href;
        // Get the string after the '?' from the URL
        var query_string = url.split(/\?([^\#]+)/)[1];

        if (!query_string) {
            return {};
        }

        return this.toObject(query_string);
    },

    setURLParams: function(params, options) {
        if (!params) {
            return false;
        }

        var options = options || {};
        options.overwrite = options.overwrite || true;

        var current_params = this.getURLParams();

        var param_keys = Object.keys(params);
        for (var i = 0; i < param_keys.length; i++) {
            var param_key = param_keys[i];
            var param_value = params[param_key];
            current_params[param_key] = !options.overwrite ? (current_params[param_key] || param_value) : param_value;
        }

        var new_param_keys = Object.keys(current_params);
        var query_array = [];
        for (var i = 0; i < new_param_keys.length; i++) {
            var param_key = new_param_keys[i];
            var param_value = current_params[param_key];
            query_array.push(encodeURIComponent(param_key) + '=' + encodeURIComponent(param_value));
        }

        var query_string = '?' + query_array.join('&');

        if (options.push_state) {
            window.history.pushState({}, null, query_string);
        } else {
            window.history.replaceState({}, null, query_string);
        }
    },

    clearURLParams: function(params, options) {
        var options = options || {};

        if (options.push_state) {
            window.history.pushState({}, null, window.location.pathname);
        } else {
            window.history.replaceState({}, null, window.location.pathname);
        }

        if (params && Object.keys(params).length) {
            // If state was pushed to clear, don't push an extra state
            options.push_state = false;
            this.setURLParams(params, options);
        }
    },

    /**
     * Creates a copy of the passed object.
     *
     * @param {object} obj - The object to clone.
     * @returns Copy of the object passed.
     */
    clone: function(obj) {
        if (null == obj || "object" != typeof obj) return obj;
        var copy = obj.constructor();
        for (var attr in obj) {
            if (obj.hasOwnProperty(attr)) copy[attr] = obj[attr];
        }
        return copy;
    },

    /**
     * Curency formating function.
     *
     * @param {(number|string)} price - The price to format.
     * @param {object} options - The options for the currency formatter.
     * @param {boolean} [options.cents=false] - Wether or not the price is currently in cents.
     * @param {string} [options.locale=Shop locale or 'en-CA'] - Override the locale to format to.
     * @param {string} [options.currency=Shop currency or 'CAD'] - Override the currency to output.
     * @param {boolean} [options.no_zeros=false] - To output the price without decimal zeros.
     * @returns {(string|null)} The price formatted based on the locale and currency.
     * @memberof Utilities
     * @example
     * // Returns '$12.56'
     * theme.utils.toMoney(12.56)
     *
     * // Returns '12,56 $'
     * theme.utils.toMoney('12.56', { locale: 'fr-CA', currency: 'CAD' })
     *
     * // Returns '$12.56'
     * theme.utils.toMoney('1256', { cents: true })
     */
    toMoney: function(price, options) {
        if (price !== 0 && !price) {
            console.error('Utilities.toMoney: Invalid price passed.', price);
            return null;
        }

        var price = new Number(price);
        var options = options || {};
        var currency_options = {
            style: 'currency'
        }

        // Assign defaults if no options are passed.
        options.cents = options.cents || false;
        options.locale = options.locale || 'en';
        currency_options.currency = options.currency || 'CAD';

        // Convert to dollars if the amount passed is in cents
        if (options.cents) {
            price = price / 100;
        }

        // Check if there are decimals and no zero option
        if (options.no_zeros && !price.toString().split('.')[1]) {
            currency_options.minimumSignificantDigits = 2;
        }

        return price.toLocaleString(options.locale, currency_options);
    },

    formatBrand: function (str) {
        return str
          .toLowerCase()
          .split('-')
          .map(function(word) {
              return word[0].toUpperCase() + word.substr(1);
          })
          .join(' ');
    }
}

var prev_arrow = '<button aria-label="prev" role="button" class="slick-slider-prev-btn"><span class="hidden">prev</span><svg data-name="Prev Arrow" xmlns="http://www.w3.org/2000/svg" width="12.464" height="22.651" viewBox="0 0 12.464 22.651"><path id="Path_14" data-name="Path 14" d="M27.911,19.533,17.7,9.342a1.134,1.134,0,0,0-1.6,1.6l9.4,9.4-9.4,9.4a1.127,1.127,0,0,0-.01,1.593l.01.01a1.134,1.134,0,0,0,1.6,0L27.911,21.106A1.126,1.126,0,0,0,27.911,19.533Z" transform="translate(-15.767 -9.037)"/></svg></button>';
var next_arrow = '<button aria-label="next" role="button" class="slick-slider-next-btn"><span class="hidden">next</span><svg data-name="Next Arrow" xmlns="http://www.w3.org/2000/svg" width="12.464" height="22.651" viewBox="0 0 12.464 22.651"><path id="Path_14" data-name="Path 14" d="M27.911,19.533,17.7,9.342a1.134,1.134,0,0,0-1.6,1.6l9.4,9.4-9.4,9.4a1.127,1.127,0,0,0-.01,1.593l.01.01a1.134,1.134,0,0,0,1.6,0L27.911,21.106A1.126,1.126,0,0,0,27.911,19.533Z" transform="translate(-15.767 -9.037)"/></svg></button>';

var prev_arrow_2 = '<button aria-label="prev" role="button" class="slick-slider-prev-btn"><span class="hidden">prev</span><svg data-name="Prev Arrow" xmlns="http://www.w3.org/2000/svg" width="12.464" height="22.651" viewBox="0 0 12.464 22.651"><path id="Path_14" data-name="Path 14" d="M27.911,19.533,17.7,9.342a1.134,1.134,0,0,0-1.6,1.6l9.4,9.4-9.4,9.4a1.127,1.127,0,0,0-.01,1.593l.01.01a1.134,1.134,0,0,0,1.6,0L27.911,21.106A1.126,1.126,0,0,0,27.911,19.533Z" transform="translate(-15.767 -9.037)"/></svg></button>';
var next_arrow_2 = '<button aria-label="next" role="button" class="slick-slider-next-btn"><span class="hidden">next</span><svg data-name="Next Arrow" xmlns="http://www.w3.org/2000/svg" width="12.464" height="22.651" viewBox="0 0 12.464 22.651"><path id="Path_14" data-name="Path 14" d="M27.911,19.533,17.7,9.342a1.134,1.134,0,0,0-1.6,1.6l9.4,9.4-9.4,9.4a1.127,1.127,0,0,0-.01,1.593l.01.01a1.134,1.134,0,0,0,1.6,0L27.911,21.106A1.126,1.126,0,0,0,27.911,19.533Z" transform="translate(-15.767 -9.037)"/></svg></button>';

var _slideBanner1 = $('.pb1-slick-slider, .bb-slide-banner-wrap');
if(_slideBanner1 && _slideBanner1.length) {
    var _autoplay1 = _slideBanner1.data("autoplay");
    var _infinite1 = _slideBanner1.data("infinite");
    var _fade1 = _slideBanner1.data("fade");
    $('.pb1-slick-slider, .bb-slide-banner-wrap').slick({
        dots: true,
        infinite: _infinite1,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: _autoplay1,
        speed: 500,
        fade: _fade1,
        cssEase: 'linear',
        adaptiveHeight: true,
        prevArrow: prev_arrow,
        nextArrow: next_arrow
    });
}

$('.pb4-slider').slick({
    dots: true,
    infinite: true,
    speed: 500,
    fade: true,
    cssEase: 'linear',
    prevArrow: prev_arrow_2,
    nextArrow: next_arrow_2
});

var _slideBanner5 = $('.pb5-slick-slider');
if(_slideBanner5 && _slideBanner5.length) {
    var _autoplay5 = _slideBanner5.data("autoplay");
    var _infinite5 = _slideBanner5.data("infinite");
    var _fade5 = _slideBanner5.data("fade");
    $('.pb5-slick-slider').slick({
        dots: true,
        infinite: _infinite5,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: _autoplay5,
        speed: 500,
        fade: _fade5,
        cssEase: 'linear',
        prevArrow: prev_arrow,
        nextArrow: next_arrow
    });
}

var _slideBanner5s = $('.pb5s-slick-slider');
if(_slideBanner5s && _slideBanner5s.length) {
    var _autoplay5s = _slideBanner5s.data("autoplay");
    var _infinite5s = _slideBanner5s.data("infinite");
    var _fade5s = _slideBanner5s.data("fade");
    $('.pb5s-slick-slider').slick({
        dots: true,
        infinite: _infinite5s,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: _autoplay5s,
        speed: 500,
        fade: _fade5s,
        cssEase: 'linear',
        prevArrow: prev_arrow,
        nextArrow: next_arrow
    });
}


var _slideBanner = $('.cms-banners_inner');
if(_slideBanner && _slideBanner.length) {
    var _autoplay = _slideBanner.data("autoplay");
    var _infinite = _slideBanner.data("infinite");
    var _fade = _slideBanner.data("fade");

    _slideBanner.slick({
        dots: false,
        infinite: _infinite,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: _autoplay,
        speed: 500,
        fade: _fade,
        cssEase: 'linear',
        prevArrow: prev_arrow,
        nextArrow: next_arrow
    });
}



$('.pb6-btn, .left-pb6-btn').on('click', function() {
    $('.pb6-wrap').toggleClass('show');
});
$('.pb6-black-overlay').on('click', function() {
    $('.pb6-wrap').removeClass('show');
});

$('.timer-close-btn').on('click', function (){
   $('.timer').toggleClass('hide');
    $('.timer').parent('.shopify-section').toggleClass('is-hidden');
});

/* my account */

theme.customerAccount = (function() {
    $('.address-edit-toggle').on('click', function() {
        var formId = $(this).data('form-id');
        $('#AddressEdit_' + formId).toggleClass('hide');
    });

    $('.prestige-card__form .button--edit').on('click', function() {
        // Hide the Edit button, and show the Save button.
        $(this).hide();
        $('.prestige-card__form .button--save').show();
        // Make the prestige number input editable.
        $('input[name=card_number]').prop('readonly', false).removeClass('error');
        $('.prestige-error').remove();
    });

    $('.address-delete').on('click', function() {
        var $el = $(this);
        var formId = $el.data('form-id');
        var confirmMessage = $el.data('confirm-message');
        if (confirm(confirmMessage || 'Are you sure you wish to delete this address?')) {
            Shopify.postLink('/account/addresses/' + formId, {parameters: {_method: 'delete'}});
        }
    });

})();

theme.customerAddresses = (function() {
    var $newAddressForm = $('#AddressNewForm');

    if (!$newAddressForm.length) {
        return;
    }

    // Initialize observers on address selectors, defined in shopify_common.js
    if (Shopify) {
        new Shopify.CountryProvinceSelector('AddressCountryNew', 'AddressProvinceNew', {
            hideElement: 'AddressProvinceContainerNew'
        });
    }

    // Initialize each edit form's country/province selector
    $('.address-country-option').each(function() {
        var formId = $(this).data('form-id');
        var countrySelector = 'AddressCountry_' + formId;
        var provinceSelector = 'AddressProvince_' + formId;
        var containerSelector = 'AddressProvinceContainer_' + formId;

        new Shopify.CountryProvinceSelector(countrySelector, provinceSelector, {
            hideElement: containerSelector
        });
    });

    // Toggle new/edit address forms
    $('.address-new-toggle').on('click', function() {
        $newAddressForm.toggleClass('hide');
    });


    $('.address-edit-toggle').on('click', function() {
        var formId = $(this).data('form-id');
        $('#EditAddress_' + formId).toggleClass('hide');
    });

    $('.address-delete').on('click', function() {
        var $el = $(this);
        var formId = $el.data('form-id');
        var confirmMessage = $el.data('confirm-message');
        if (confirm(confirmMessage || 'Are you sure you wish to delete this address?')) {
           Shopify.postLink('/account/addresses/' + formId, {parameters: {_method: 'delete'}});
        }
    });
})();


$('.btn-toggle_search-mobile').on('click', function() {
    var _form = $('.site-header_icons-wrapper');
    if(_form && _form.length) {
        _form.toggleClass('open');
        if (_form.hasClass('open')) {
            $('.search-bar_input').focus();
            $('form.search-form').addClass('active')
        } else {
            $('form.search-form').removeClass('active')
        }
    }
    var _nav = $('.mobile-nav--close');
    if(_nav && _nav.length) {
        _nav.trigger('click');
    }
});

$('.btn--link-menu').on('click', function() {
    var _search = $('.site-header_icons-wrapper');
    if(_search && _search.length) {
        _search.removeClass('open');
    }
});


/* size chart */
$(function () {

    const cm_unit = 2.54;
    const inch_unit = 0.393701;
    var $general_table = $('.js-sc-tables.general-table');
    var $curve_appeal_table = $('.js-sc-tables.curve-appeal-table');
    var $tables = $('.js-sc-tables');
    var $category_inputs = $('.js-sc-category-inputs input');
    var $table_sections = $('.js-sc-table-section');
    var $products_types = $('.js-product-type');
    var $modal_close = $('.js-modal-close');
    var $modal_link = $('.js-modal-link');
    var $unit_form_inputs = $('.js-sc-unit-form input');

    $modal_link.click(function (e) {
        e.preventDefault();
        var $this = $(this);
        var target = $this.data('modal-target');
        var types = $products_types.data('product-types').split('-');
        var category = "";

        // Creates a list of all the existing categories
        var categories = $.map( $category_inputs, function( n ) {
            return $(n).val();
        });

        category = types.find(function(product_type) {
            return categories.indexOf(product_type) > -1; // Returns the matching category from the list
        });

        $('#sc-category-' + category).prop('checked', true).change(); // Triggers the category tabs
        $('.js-modal' + target).addClass('show');
    });

    $modal_close.click(function (e) {
        e.preventDefault();
        var $this = $(this);
        var target = $this.closest('.js-modal').removeClass('show');
    });

    $unit_form_inputs.change(function () {
        var unit_name = $(this).val();
        var title = $('.unit-converter__header-unit').data('sc-' + unit_name + '-title');

        $('.unit-converter__header-unit').text(title);
        $('.js-sc-unit').each(function () {
            sizeUnitConverter($(this), unit_name);
        });
    });

    $category_inputs.change(function () {
        var category = $(this).val();
        categorySwitcher(category);
    });

    // Switches when a category is selected
    // Filters the needed tables for that category
    function categorySwitcher(category) {
        $table_sections.each(function () {
            var $table = $(this);
            var $table_body = $table.find('tbody');
            var table_filters = $table.data('sc-section-filters').split(',');
            var $row_filters = $table.data('hide-row').split(',');

            // Resets the filter functionality
            $table.hide();
            $table_body.find('tr').show();

            table_filters.forEach(function (table_filter) {
                if (category == table_filter) {
                    $table.show();

                    $row_filters.forEach(function (row_filter) {
                        var filter_array = row_filter.split(':');
                        var category_name  = filter_array[0];
                        var row_name = filter_array[1];

                        // Hide data of a table depending on the category
                        if (category == category_name) {
                            $table_body.find('tr[data-row-name="' + row_name  + '"]').hide();
                        }
                    });
                }
            });
        });
    }

    // Converts the tables units' from inch to cm
    // and vice-versa
    function sizeUnitConverter($element, unit_name) {
        var table_data = $element.text().split('-');
        var new_data = '';

        table_data.forEach(function (data,i) {

            // Will not run if the data is not a number
            if ($.isNumeric(data)) {
                var unit = parseFloat(data); // table data
                var cm_to_inch = parseFloat(unit * inch_unit).toFixed(1).toString().split('.'); // Makes an array of integers
                var inch_to_cm = parseFloat(unit * cm_unit).toFixed(1).toString().split('.'); // Makes an array of integers
                var decimal_point = '.';
                var separator = '-'
                var new_unit = '';

                if (unit_name == 'inch') {

                    // Remmoves trailing zeros from the number
                    if (cm_to_inch[1] == 0) {
                        cm_to_inch[1] = '';
                        decimal_point = ''
                    }

                    new_unit = cm_to_inch[0] + decimal_point + cm_to_inch[1]
                } else if (unit_name == 'cm') {

                    // Remmoves trailing zeros from the number
                    if (inch_to_cm[1] == 0) {
                        inch_to_cm[1] = '';
                        decimal_point = '';
                    }

                    new_unit = inch_to_cm[0] + decimal_point + inch_to_cm[1];
                }

                if ( i >= table_data.length -1 ){
                    separator = '';
                }
                new_data += new_unit + separator;
                $element.text(new_data);
            }
        });
    }

});
/* end size chart */
// =================================================
// Newsletter email input trigger
$(function() {

    var $newsletter_wrapper = $('.js-newsletter');
    var $newsletter_buttons = $('.js-newsletter-button');
    var $newsletter_input = $newsletter_wrapper.find('.js-newsletter-email');
    var url = $newsletter_wrapper.data('newsletter-url');


    if (window.KlaviyoSubscribe) {
        KlaviyoSubscribe.attachToModalForm('#k_id_modal', {
            delay_seconds: 1
        });
    }

    // Form will be submitted when the enter is pressed
    $newsletter_input.keyup(function(e) {
        e.preventDefault();
        // Code for enter key
        if (e.keyCode === 13) {
            var newsletter_wrapper = $(this).closest('.js-newsletter');
            $newsletter_buttons.trigger('newsletter-submit', newsletter_wrapper);
        }
    });

    $newsletter_buttons.click(function(e){
        var newsletter_wrapper = $(this).closest('.js-newsletter');
        $(document).trigger('newsletter-submit', newsletter_wrapper);
    });

    $(document).on('newsletter-submit', function(e, newsletter_target){
        e.preventDefault();

        var $newsletter_form = $(newsletter_target).find('.js-newsletter-form');
        var $email = $newsletter_form.find('.js-newsletter-email');
        var $page_source = $newsletter_form.find('.js-newsletter-source');

        var data = {
            attributes: {
                "klaviyo_email": $email.val() || '',
                "klaviyo_source": $page_source.val() || ''
            }
        }

        $.post('/cart/update.json', data)
            .done(function() {
                KlaviyoSubscribe.setClosedModalCookie();
                window.location.href = url;
            });
    });

});

// Email form validation and form submission

$(function () {
    if (window.KlaviyoSubscribe) {
        KlaviyoSubscribe.attachToForms('#klaviyo-sign-up-form');
        var $klaviyo_form = $('.js-sign-up-form');
        var $legal_checbox = $('.js-email-sign-up-legal');
        var $submit_form_button = $('.js-email-sign-up-submit');

        function checkInput() {
            var legal1 = $('#sign-up-legal-1').prop('checked');
            var legal2 = $('#sign-up-legal-2').prop('checked');
            var legal3 = $('#sign-up-legal').prop('checked');

            if ((legal3 && legal1) || (legal3 && legal2)) {
                $submit_form_button.removeClass('form-submit-button--disabled').prop('disabled', false);
            } else {
                $submit_form_button.addClass('form-submit-button--disabled').prop('disabled', true);
            }
        }

        $legal_checbox.on('change', function () { checkInput()});
        $('#sign-up-legal-1').on('change', function () { checkInput()});
        $('#sign-up-legal-2').on('change', function () { checkInput()});

        $klaviyo_form.on("submit", function (e) {
            e.preventDefault();
            var $first_name = $('[name="first_name"]');
            var $last_name = $('[name="last_name"]');
            var $email = $('[name="email"]');
            var $birthday = $('[name="Birthday"]');
            var $zip_code = $('[name="zipcode"]');
            var $errors = $('.js-form-errors');
            var $success_link = $('.js-success-link');
            $errors.empty();
            $errors.hide();

            if (!$email.val().trim()) {
                erroDisplay($errors, $email);
            }

            if (!$first_name.val().trim()) {
                erroDisplay($errors, $first_name);
            }

            if (!$last_name.val().trim()) {
                erroDisplay($errors, $last_name);
            }

            if (!$zip_code.val().trim() || !isValidPostalCode($zip_code.val().trim())) {
                erroDisplay($errors, $zip_code);
            }

            if ($birthday.val().trim() && !isValidDate($birthday.val().trim())) {
                erroDisplay($errors, $birthday);
            }

            $errors.show();
            var error_count = $errors.find('.form-error');

            if (error_count.length > 0) {
                return;
            }


            var serialize_data =  $(this).serialize();

            serialize_data = serialize_data.replace('Brand=Suzy+Shier&Brand=Le+Chateau', 'Brand=%5B%22%5B%5C%22Suzy+Shier%5C%22%5D%22%2C+%22%5B%5C%22Le+Chateau%5C%22%5D%22%5D');

            var settings = {
                'async': true,
                'crossDomain': true,
                'url': 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe',
                'method': 'POST',
                'headers': {
                    'content-type': 'application/x-www-form-urlencoded',
                    'cache-control': 'no-cache'
                },
                'data': serialize_data
            };
            $.ajax(settings).done(function (resp) {
                if (resp.success) {
                    $errors.hide();
                    $klaviyo_form.trigger("reset");
                    window.location.replace($success_link.data('success-link'));
                } else {
                    $errors.show();
                }
            });

            function isValidDate(date_string) {
                var date_regex = /^(?:(?:31(-)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(-)(?:0?[1,3-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)\d{2})$|^(?:29(-)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(-)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)\d{2})$/;
                return date_string.match(date_regex) != null;
            }

            function isValidPostalCode(postal_code_string) {
                postal_code_string = postal_code_string.toUpperCase();
                var postal_code_regex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/;
                var zip_code_regex = /^[0-9]{5}(?:-[0-9]{4})?$/;
                return postal_code_string.match(postal_code_regex) != null || postal_code_string.match(zip_code_regex) != null;
            }

            function erroDisplay($error_container, $error_field) {
                var $list_item = $('<li>', {
                    "class": 'form-error'
                });
                $error_container.append($list_item.text($error_field.data('formError')));
            }
        });
    }
});



/*
$(function() {
    if (window.KlaviyoSubscribe) {
        KlaviyoSubscribe.attachToForms('#klaviyo-sign-up-form');

        var $klaviyo_form = $('.js-sign-up-form');
        var $legal_checbox = $('.js-email-sign-up-legal');
        var $submit_form_button = $('.js-email-sign-up-submit');

        $legal_checbox.on('change',function(e) {
            $submit_form_button
                .toggleClass('form-submit-button--disabled')
                .prop('disabled', !$(this).prop('checked'));
        });

        $klaviyo_form.on("submit", function(e) {
            e.preventDefault();

            var $first_name = $('[name="fname"]');
            var $last_name = $('[name="lname"]');
            var $email = $('[name="email"]');
            var $birthday = $('[name="birthday"]');
            var $zip_code = $('[name="zipcode"]');
            var $errors = $('.js-form-errors');
            var $success_link =  $('.js-success-link');

            $errors.empty();
            $errors.hide();

            if(!$email.val().trim()) {
                erroDisplay($errors, $email);
            }

            if(!$first_name.val().trim()) {
                erroDisplay($errors, $first_name);
            }

            if(!$last_name.val().trim()) {
                erroDisplay($errors, $last_name);
            }

            if(!$zip_code.val().trim() || !isValidPostalCode($zip_code.val().trim())) {
                erroDisplay($errors, $zip_code);
            }

            if($birthday.val().trim() && !isValidDate($birthday.val().trim())) {
                erroDisplay($errors, $birthday);
            }

            $errors.show();
            var error_count = $errors.find('.form-error');

            if (error_count.length > 0 ) {
                return;
            }

            var settings = {
                'async': true,
                'crossDomain': true,
                'url': 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe',
                'method': 'POST',
                'headers': {
                    'content-type': 'application/x-www-form-urlencoded',
                    'cache-control': 'no-cache'
                },
                'data': $(this).serialize()
            };

            $.ajax(settings)
                .done(function (resp) {
                    if (resp.success) {
                        $errors.hide();
                        $klaviyo_form.trigger("reset");
                        window.location.replace($success_link.data('success-link'));
                    } else {
                        $errors.show();
                    }
                });

            function isValidDate(date_string) {
                var date_regex = /^(?:(?:31(-)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(-)(?:0?[1,3-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)\d{2})$|^(?:29(-)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(-)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)\d{2})$/;
                return date_string.match(date_regex) != null;
            }

            function isValidPostalCode(postal_code_string) {
                postal_code_string = postal_code_string.toUpperCase();
                var postal_code_regex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/;
                var zip_code_regex = /^[0-9]{5}(?:-[0-9]{4})?$/;

                return postal_code_string.match(postal_code_regex) != null || postal_code_string.match(zip_code_regex) != null;
            }


            function erroDisplay($error_container, $error_field) {
                var $list_item = $('<li>', {class:'form-error'});
                $error_container.append($list_item.text($error_field.data('formError')));
            }
        });
    }
});
*/

// Calendar input selector
$(function() {

    var lang = '';
    var $calendar_wrapper = $('.js-sign-up-calendar');
    var $calendar_button = $calendar_wrapper.find('.js-sign-up-calendar-button');
    var calendar_format =  $calendar_wrapper.data('date-format')
    var $calendar = $calendar_wrapper.find('[data-toggle="datepicker"]');
    var today = new Date();
    var dd = today.getDate();
    var mm = today.getMonth()+1;
    var yyyy = today.getFullYear();

    if (window.theme.locale == 'en') {
        lang = 'en-US'
    } else if (window.theme.locale == 'fr') {
        lang = 'fr-FR'
    }

    $calendar.datepicker({
        language: lang,
        format: calendar_format,
        autoHide: true,
        trigger: $calendar_button,
        date: new Date(1990, 1, 1),
        endDate: new Date(yyyy,mm,dd)
    });

    var $klaviyo_form = $('.js-sign-up-form');
    var $legal_checbox = $('.js-email-sign-up-legal');
    var $submit_form_button = $('.js-email-sign-up-submit');

    function checkInput() {
        var legal1 = $('#sign-up-legal-1').prop('checked');
        var legal2 = $('#sign-up-legal-2').prop('checked');
        var legal3 = $('#sign-up-legal').prop('checked');

        if ((legal3 && legal1) || (legal3 && legal2)) {
            $submit_form_button.removeClass('form-submit-button--disabled').prop('disabled', false);
        } else {
            $submit_form_button.addClass('form-submit-button--disabled').prop('disabled', true);
        }
    };

    $legal_checbox.on('change', function () { checkInput()});
    $('#sign-up-legal-1').on('change', function () { checkInput()});
    $('#sign-up-legal-2').on('change', function () { checkInput()});
});


/*================ PROMOTION  ================*/
/*================ Templates  ================*/
/*================ Templates ================*/

!function (promoMessaging, $) {
    var prod_messaging = {};
    var campaigns = window.CAMPAIGN_MESSAGING || [];

    function moneyFREN(m) {
        var is_fr = window.Shopify.locale === 'fr';
        return is_fr ? m.toFixed(2) + ' $' : '$' + m.toFixed(2);
    }

    var getMessages = function getMessages() {
        return $('[data-promo-message]');
    }; // Promo messaging object


    var promoMessage = function promoMessage(config) {
        var promoMessage = this;
        var config = config || {};

        var setPromo = function setPromo() {
            for (var i = 0; i < campaigns.length; i++) {
                var campaign = campaigns[i];

                if (campaign.hasOwnProperty('include') && campaign.include.hasOwnProperty('collection_includes') && campaign.hasOwnProperty('display_messaging') && !campaign.include.hasOwnProperty('discount_code') && campaign.display_messaging) {
                    var collections = campaign.include.collection_includes || [];
                    var exclusions = campaign.exclude && campaign.exclude.collection_includes ? campaign.exclude.collection_includes : [];
                    var included = false;
                    var excluded = false; // Get inclusions

                    for (var j = 0; j < collections.length; j++) {
                        var collection = collections[j];

                        if (promoMessage.collections.indexOf(collection) >= 0) {
                            included = true;
                            break;
                        }
                    } // Get exclusions


                    for (var j = 0; j < exclusions.length; j++) {
                        var exclusion = exclusions[j];

                        if (promoMessage.collections.indexOf(exclusion) >= 0) {
                            excluded = true;
                            break;
                        }
                    }

                    if (included && !excluded) {
                        var promo_object = {
                            regular_priced: campaign.regular_priced_only,
                            messaging: campaign.message,
                            type: campaign.type,
                            colors: campaign.colors,
                            calculate_price: campaign.calculate_price
                        };

                        if (campaign.discount) {
                            var amount = campaign.discount.match(/\(([\d|\.]+)\)/)[1];
                            promo_object.discount = {
                                amount: parseFloat(amount),
                                type: campaign.discount.split('Discount')[0].toLowerCase()
                            };
                        }

                        return promo_object;
                    }
                }
            }

            return false;
        };

        promoMessage.collections = config.collections || [];
        promoMessage.id = config.id;
        promoMessage.sale = config.sale || false;
        promoMessage.promo = setPromo();
    }; // Update promo message based on element

   promoMessaging.updateCategIds = function(){
        var _items = $('.plp-spot-products .product.featured-collection__info');
        if(_items.length) {
            _items.each(function(){
                var _form = $(this).find('.product--image-wrap form[data-product-form]');
                if(_form && _form.length) {
                    var _collection_ids = _form.find('input[name="properties[_collection_ids]"]');
                    var _handle = _form.find('input[name="properties[_collection_handle]"]');
                    if(_collection_ids && !_collection_ids.val()) {
                        promoMessaging.getPromo(_handle.val())
                            .done(function(resp) {
                                // Test to make sure the proper data is sent back
                                var regEx = /^\n<!doctype html>/;
                                if (regEx.test(resp)) {
                                    return false;
                                }
                                var start_ = resp.indexOf('class="shopify-section">') + 24;
                                var end_ = resp.length - 6;
                                var resp_json = resp.substring(start_, end_);
                                try {
                                    var object = JSON.parse(resp_json);
                                    _collection_ids.val(object.collections.join(','));
                                } catch(e) {
                                    return false
                                }
                            });
                    }
                }
            });
        }
    };

    promoMessaging.showMessages = function () {
        promoMessaging.updateCategIds();

        $.each(prod_messaging, function (id, promoMessage) {
            if (promoMessage.promo && !(promoMessage.sale && promoMessage.promo.regular_priced)) {
                var $message = $('.messaging-' + promoMessage.id);
                $message.removeClass('messaging-' + promoMessage.id);
                $message.html('<span class="promo-message" ' + (promoMessage.promo.colors ? 'style="background-color: ' + promoMessage.promo.colors.background + ';color: ' + promoMessage.promo.colors.text + '"' : '') + '>' + promoMessage.promo.messaging + '</span>');
                if (promoMessage.promo.calculate_price) {
                    var $price = $message.closest('.js-product').find('.js-product-price');
                    var price = $price.data('product-price');

                    if (!!price && typeof price !== 'number') {
                        price = price.match(/\d+/g).join('');
                    }

                    try {
                        var old_price = parseFloat(price / 100);
                        var new_price = old_price;

                        switch (promoMessage.promo.discount.type) {
                            case 'percentage':
                                new_price = old_price - old_price * (promoMessage.promo.discount.amount / 100);
                                break;

                            case 'money':
                                new_price = old_price - promoMessage.promo.discount.amount;
                                break;

                            case 'price':
                                new_price = promoMessage.promo.discount.amount;
                                break;
                        }

                        if ($price.data("main-price")) {
                            $("[type='application/ld+json']").each((index, element) => {
                                let jsonData = JSON.parse(element.textContent);
                                if (jsonData["@type"] == "Product") {
                                    jsonData.offers.forEach(product => {
                                        product.price = new_price;
                                    });
                                }
                                element.textContent = JSON.stringify(jsonData)
                            });

                        }

                        if (!$price.find('.discounted').length && old_price > new_price) {
                            $price.html('<span class="sale-wrapper"><span class="product__price2">' + $price.html().trim() + '</span><span class="money discounted">' + moneyFREN(new_price) + '</span></span>');
                        } else {
                            if ($price.length>1) {
                                var html_old_proce = $price.find('.product__price2').html();
                               // $price.eq(1).html('<span class="sale-wrapper"><span class="product__price2">' + moneyFREN(old_price) + '</span><span class="discounted">' + moneyFREN(new_price) + '</span></span>')
                                $price.html('<span class="sale-wrapper"><span class="product__price2">' +
                                  ( $price.find('.product__price2').length
                                        ? html_old_proce
                                        : moneyFREN(old_price) ) +
                                  '</span><span class="money discounted">' + moneyFREN(new_price) + '</span></span>')
                            }
                        }

                        if ($message.closest('.product-single__meta').length) {

                            let discounted_price_klarna = parseInt(new_price*100);
                            let prestige_procent = parseInt($('#member-perk').attr('data-procent'));
                            let price_membership = new_price - parseInt(new_price * prestige_procent)/100;
                            var _p = parseInt(price_membership*100)/100;

                            $('#member-perk').html(
                               moneyFREN(_p)
                            );


                            //we need interval instead of timeout because when scrolling/clicking the page - component rerenders
                            //the block resets to the default value without checking changed
                            //Klarna doesn't want any message interventions, so they always try to reset the price
                            //this way we "hack" component to display our special price
                            //since we don't know when it changes, we do it this way
                            /*
                            setInterval(() => {

                                let klarna_widget = document.getElementById("klarna--placement");

                                if(klarna_widget && window.Klarna) {
                                    let data_klarna_prive = parseInt(klarna_widget.dataset.purchaseAmount);
                                    window.d_p_klarna = window.d_p_klarna ? window.d_p_klarna : 0;
                                    if (data_klarna_prive !== discounted_price_klarna ) {
                                        window.d_p_klarna = discounted_price_klarna;
                                        klarna_widget.dataset.purchaseAmount = discounted_price_klarna;
                                        window.Klarna.OnsiteMessaging && window.Klarna.OnsiteMessaging.refresh();
                                    }
                                }

                            }, 500);
                            */
                        }
                    } catch (e) {}
                }
            }
            delete prod_messaging[id];
        });
    };

    promoMessaging.addMessage = function ($element, config) {
        if (!prod_messaging[config.id]) {
            prod_messaging[config.id] = new promoMessage(config);
        } // Add class for selection

        $element.addClass('messaging-' + config.id);
    }; // Update the list of products


    promoMessaging.update = function () {
        var deferred = [];
        getMessages().each(function () {
            var $this = $(this);
            var config = $this.data('promo-message');
            $this.removeAttr("data-promo-message");
            config.id = config.product || Math.floor(Math.random() * 1000000);
            if (config && config.collections !== undefined) {
                promoMessaging.addMessage($this, config);
            } else if (config.handle || config.url) {
                var handle = !!config.handle ? config.handle : config.url.split('products/')[1].split('?')[0];

                // Fetch the product data
                if (handle && !prod_messaging[config.product]) {
                    deferred.push(
                      promoMessaging.getPromo(handle)
                        .done(function(resp) {
                            // Test to make sure the proper data is sent back
                            var regEx = /^\n<!doctype html>/;

                            if (regEx.test(resp)) {
                                return false;
                            }
                            var start_ = resp.indexOf('class="shopify-section">') + 24;
                            var end_ = resp.length - 6;
                            var resp_json = resp.substring(start_, end_);
                            try {
                                var object = JSON.parse(resp_json);
                                $this.closest('.product').find('input[name="properties[_collection_ids]"]').val(object.collections.join(','));
                                promoMessaging.addMessage($this, object);
                            } catch(e) {
                                return false
                            }
                        })
                    );
                } else {
                    promoMessaging.addMessage($this, config);
                }
            }
        }); // Show messages on elements
        $.when.apply($, deferred).always(function () {
            promoMessaging.showMessages();
        });
    };

    promoMessaging.getProductMessages = function () {
        return prod_messaging;
    };

    promoMessaging.getPromo = function (handle) {
        return $.get('/products/' + handle + '?view=collections');
    };

    $(function () {
        promoMessaging.update();
    });
}(window.promoMessaging = window.promoMessaging || {}, jQuery);
/*================ END PROMOTION  ================*/
/*================ NOSTO  ================*/
(function($) {
    var nostojs = window.nostojs || false;

    if (!nostojs) {
        var name="nostojs";
        window[name]=window[name]||function(cb){(window[name].q=window[name].q||[]).push(cb);};
        nostojs = window.nostojs;
    }

    function updateNostoPromos() {
        nostojs(function(api){
            api.listen('postrender', function(nostoPostRenderEvent) {
                if (!!promoMessaging) {
                    promoMessaging.update();
                }
                addToLinkCollection();
            });
        });
    }

    function addToLinkCollection() {

        var _breadcrumb = document.querySelectorAll('a.breadcrumb-item');
        var _prefix = '';
        if(_breadcrumb && _breadcrumb.length) {
            _breadcrumb = _breadcrumb[_breadcrumb.length - 1];
            _prefix = _breadcrumb.getAttribute('href');
            _prefix = replace('/fr/','/');
        }

        var _list = document.querySelectorAll('.nosto-list-item.js-product a');
        if(_list && _list.length) {
            _list.forEach(function (item) {
                var _href = item.getAttribute('href');
                _href = _href.split('/products/').join(_prefix + '/products/');
                item.setAttribute('href', _href);
            });
        }
    }

    $(function() {
        if (!!nostojs) {
            updateNostoPromos();
        }
    });

})(jQuery);
/*================ end NOSTO  ================*/

!function ($){

    $(".reorder__move-up, .reorder__move-down").click(function () {
        var row = $(this).parents("tr:first");

        if ($(this).is(".reorder__move-up")) {
            row.insertBefore(row.prev());
        } else {
            row.insertAfter(row.next());
        }
    });

    var _colorEn = function(color) {
        if ($('html').attr('lang') === 'fr') {
            var _color = pdp_all_colors.filter(function(item) {
                return item.color_fr === color;
            });
            if(_color && _color.length) {
                return _color[0].color_en;
            }
        }
        return color;
    }

    var currentColor = window && window.current_color;
    var currentSize = window && window.current_size;
    var currentImg = null;

    var _switcher = $('[data-shitcher-store]');
    if(_switcher.length) {
        _switcher.each(function(){
            $(this).on('click', function(e){
                _switcher.removeClass('active');
                $(this).addClass('active');
                e.preventDefault();
                var _url = $(this).attr('href');
                $.post('/cart/update.js', 'attributes[site]='+ $(this).attr('data-store'))
                    .done(function() {
                        window.location.href = _url;
                    });
            })
        });
    }

    if(currentColor || currentSize){
        var is_fr = $('html').attr('lang') === 'fr';

        var pdpFormTop = function () {
            var _form = $('form[id^="product_form_"]');
            var _headerPdp = _form.find($('.form-buttons_add'));
            var _header = $('#shopify-section-header-suzy, #shopify-section-header-chateau');

            if (_headerPdp) {
                var __div = $('.top-pdp_sticky');
                if (__div) {
                    __div.remove();
                }

                var _swatch = $('.poc-colors').find('input:checked').parent().html();
                var id_ = $('.poc-colors').find('input:checked').attr('id');
                if(_swatch !== undefined) { _swatch = _swatch.replace(/id_/g, id_+'-sticky') };
                var _leftHtml = '<div class="pdp-sticky-left">';
                _leftHtml += currentImg;
                _leftHtml += '<p class="h3">' + $('.product-single__title').text() + '</p>';
                _leftHtml +=  $('.js-product-price').html();
                _leftHtml += '</div>';

                var _righttHtml = '<div class="pdp-sticky-right">';
                if(_swatch !== undefined) {
                    _righttHtml += '<div class="swatch-item _scroll">' + _swatch + '</div>';
                }
                if(currentColor) {
                    if(!currentSize) {
                        var _currentColorTop = '$' + currentColor;
                        if(is_fr) {
                            _currentColorTop = currentColor + ' $';
                        }
                        var _label = $('label[for="SingleOptionSelector-0"]').text();
                        _righttHtml += '<div class="option-1 _scroll">' + _label +': '+_currentColorTop + '</div>';

                    } else {
                        _righttHtml += '<div class="option-1 _scroll">' + currentColor + '</div>';

                    }
                }
                if(currentSize) {
                    _righttHtml += '<div class="option-2 _scroll">' + currentSize + '</div>';
                }


                _righttHtml += '<div class="form-buttons_add-sticky"></div>';
                _righttHtml += '</div>';

                var  _div = $('<div/>')
                    .addClass('top-pdp_sticky page-width')
                    .html(_leftHtml + _righttHtml)
                    .appendTo(_header);


                $.fn.isInViewport = function () {
                    let elementTop = $(this).offset().top;
                    let elementBottom = elementTop + $(this).outerHeight();

                    let viewportTop = $(window).scrollTop() + 125;
                    let viewportBottom = viewportTop + window.innerHeight;
                    
                    if (viewportTop < 400) return true;
                    return elementBottom > viewportTop && elementTop < viewportBottom;
                };

                $('._scroll').on('click', function(){
                    window.scroll({
                        top: 0,
                        left: 0,
                        behavior: 'smooth'
                    });
                });

                var _updateHeaderPdp = function (elem) {

                    elem.each(function () {
                        if ($(this).isInViewport()) {
                            $('.form-buttons_add-sticky').empty();
                            _div.removeClass('active');
                        } else {
                            var _clone = _headerPdp.clone(true);
                            $('.form-buttons_add-sticky').html(_clone);
                            var _button = _clone.find('button, input');
                            _button.on('click', function(){
                                var _buttonTrigger = _headerPdp.find('[class="'+ $(this).attr('class') +'"]');
                                _buttonTrigger.trigger('click');
                            });
                            _div.addClass('active');
                        }
                    })
                };

                _updateHeaderPdp(_form);

                $(window).on('scroll resize', function () {
                    _updateHeaderPdp(_form);
                });
            }
        }

        var _bdClick = function(img){
            var _mobileContainer = $('.mobile-media');

            var _pinchBlock = $('<div/>')
                .addClass('pinch-zoom')
                .appendTo(_mobileContainer);

            var _pinchContent = $('<div/>')
                .addClass('pinch-zoom_content')
                .appendTo(_pinchBlock);

            var _pinchContentImg = $('<div/>')
                .addClass('pinch-zoom_content-img')
                .appendTo(_pinchContent);
            img
                .attr('id', 'zoom-pinch-img')
                .appendTo(_pinchContentImg);

            var close_pinch = $('.close-pinch');
            close_pinch.addClass('open')
                .on('click', function(){
                    $(this).removeClass('open');
                    _pinchBlock.remove();
                })
                .appendTo(_mobileContainer);
            pinchElement();

        }
      
    var zoomMedia = function () {
            var width = $(window).width();
            var _contentZoom = $('.zoom-container');
            var _actionZoom = $('.media-zoom-item');
            //var img_just_one = _actionZoom.length === 1 ? 'img1' : null;
            var img_just_one = null;
            var _desktopContainer = $('.desktop-media');
            var _mobileContainer = $('.mobile-media');
            var _defaultContainer = $('.default-media-container');
            var _noImageInit = $('.pdp-big-images-wrap');
            if(_noImageInit.length) {
                _noImageInit.remove();
            }
            if(width > 767) {
                if(!_desktopContainer.length) {
                    _defaultContainer.empty();
                    if(_mobileContainer.length) {
                        _mobileContainer.remove();
                    }
                    _desktopContainer = $('<div/>').addClass('desktop-media');
                    if (img_just_one) _desktopContainer.addClass(img_just_one);
                    _desktopContainer.insertAfter(_defaultContainer);

                    var _actionZoom2 = _actionZoom.filter(function (index, item) {
                        return ($(item).find('.product-single__media-wrapper').attr('id') != '' && $(item).find('.product-single__media-wrapper').attr('id') != undefined)
                    });
                    _actionZoom = _actionZoom2;

                    var currentColorLang = currentColor;

                    if (is_fr) {
                        var p_ = pdp_all_colors.filter(function(col) { return col.color_fr === currentColor });
                        if (p_ && p_[0])  currentColorLang = p_[0].color_en;
                    }
                    currentColorLang = currentColorLang.replaceAll(' ', '-')
                    if(_actionZoom && _actionZoom.length) {
                        _actionZoom.each(function() {
                            if($(this).hasClass(currentColorLang) || $(this).hasClass(product.title)) {
                                $(this).appendTo(_desktopContainer);
                                $(this).on('click', function(){
                                    var $this = $(this);
                                    var _clone = $this.find('.product-single__media-wrapper');
                                    var _img = $(this)
                                        .find('img').clone(true)
                                        .appendTo($(this));
                                    var _content = $('<div/>')
                                        .addClass('content')
                                        .appendTo(_contentZoom)
                                        .on('click', function (){
                                            $this.empty();
                                            _contentZoom.empty();
                                            _contentZoom.removeClass('open');
                                            //$('body').removeClass('popup-open');
                                            _clone.appendTo($this);
                                        });

                                    var _close = $('<span/>')
                                        .addClass('close')
                                        .appendTo(_content)
                                        .on('click', function (){
                                            $this.empty();
                                            _contentZoom.empty();
                                            _contentZoom.removeClass('open');
                                            //$('body').removeClass('popup-open');
                                            _clone.appendTo($this);
                                        });

                                    _clone.appendTo(_content);
                                    _contentZoom.addClass('open');
                                    //$('body').addClass('popup-open');
                                })
                            } else {
                                $(this).appendTo(_defaultContainer);
                            }
                        });
                    }
                }
                var _noImages = _desktopContainer.find(_actionZoom);
                if(!_noImages.length){
                    if(noImage) {
                        var _noImage = $('<div>')
                            .addClass('pdp-big-images-wrap')
                            .insertBefore(_defaultContainer);
                        var _noImageContent = $('<div>')
                            .addClass('pdp-big-images')
                            .html('<img alt="no-image" src="'+ noImage +'">')
                            .appendTo(_noImage);
                    }
                }
            } else {
                if(!_mobileContainer.length) {
                    _defaultContainer.empty();
                    if(_desktopContainer.length) {
                        _desktopContainer.remove();
                    }
                    _mobileContainer = $('<div/>')
                        .addClass('mobile-media')
                        .insertAfter(_defaultContainer);

                    var _actionZoom2 = _actionZoom.filter(function (index, item) {
                        return ($(item).find('.product-single__media-wrapper').attr('id') != '' && $(item).find('.product-single__media-wrapper').attr('id') != undefined)
                    });
                    _actionZoom = _actionZoom2;

                    var currentColorLang = currentColor;
                    if (is_fr) {
                        var p_ = pdp_all_colors.filter(function(col) { return col.color_fr === currentColor });
                        if (p_ && p_[0])  currentColorLang = p_[0].color_en;
                    }

                    currentColorLang = currentColorLang.replaceAll(' ', '-')

                    if(_actionZoom && _actionZoom.length) {
                        _actionZoom.each(function() {
                            if($(this).hasClass(currentColorLang) || $(this).hasClass(product.title)){
                                $(this).appendTo(_mobileContainer);
                                $(this).on('click', function(){
                                    var $this = $(this);
                                    var _img = $this.find('img');
                                    _bdClick(_img.clone( true ));
                                    //$('body').addClass('popup-open');
                                })
                            } else {
                                $(this).appendTo(_defaultContainer);
                            }
                        });
                    }
                    _mobileContainer.slick({
                        dots: true,
                        infinite: true,
                        fade: false,
                        cssEase: 'linear',
                        speed: 250,
                        prevArrow: prev_arrow,
                        nextArrow: next_arrow
                    });
                }
                var _noImages = _mobileContainer.find(_actionZoom);
                if(!_noImages.length){
                    if(noImage) {
                        var _noImage = $('<div>')
                            .addClass('pdp-big-images-wrap')
                            .insertBefore(_defaultContainer);
                        var _noImageContent = $('<div>')
                            .addClass('pdp-big-images')
                            .html('<img alt="no-image" src="'+ noImage +'">')
                            .appendTo(_noImage);
                    }
                }
            }
        }

        $('.poc-colors .swatch-item:not(.hidden) [data-index=option1]').change(function(){
            var _mobileContainer = $('.mobile-media');
            var _defaultContainer = $('.default-media-container');
            var _desktopContainer = $('.desktop-media');
            var _actionZoom = $('.media-zoom-item');
            _defaultContainer.empty()
            if(_actionZoom && _actionZoom.length) {
                _actionZoom.each(function () {
                    $(this).appendTo(_defaultContainer);
                })
            }
            if(_mobileContainer.length) {
                _mobileContainer.remove();
            }
            if(_desktopContainer.length) {
                _desktopContainer.remove();
            }
            currentColor = $(this).val();
            $('[name="properties[_option1]"]').val(currentColor);
            zoomMedia();
            selectedVariant();
        });

        if(currentSize) {
            $('[data-index=option2]').change(function(){
                currentSize =  $(this).val();
                $('[data-current-size]').text(currentSize);
                $('[name="properties[_option2]"]').val(currentSize);
                selectedVariant();
            });
        } else {
            $('[data-index=option1]').change(function(){
                var _currentColor = '$' + $(this).val();
                if(is_fr) {
                    _currentColor = $(this).val() + ' $';
                }

                $('[data-current-size]').text(_currentColor);
                selectedVariant();
            });
        }

        var selectedVariant = function () {
            var _qtyContent = $('.qty-variant-left');
            _qtyContent.empty();
            var _select = $('.product-form__variants');
            var _option = _select.find('option[value="' + _select.val() + '"]');
            var _qty = _option.attr('data-qty');

            if(_qty < 6 && _qty > 0){
                _qtyContent.html('<span>' + stockQty.replace('%1', _qty) + '</span>')
            }

            $('[data-variant-id]').removeClass('disabled');
            $('.data-item-available').remove();

            var currentColorLang = currentColor;
            if (is_fr && $('.poc-title-selected').text() !== currentColor) {
                var p_ = pdp_all_colors.filter(function(col) { return col.color_en === currentColor });
                if (p_ && p_[0])  currentColorLang = p_[0].color_fr;
            };

            product.variants.forEach(function (item) {
                if(item.option1 === currentColorLang && item.option2 === currentSize) {
                    if(!item.available) {
                        $('[data-current-size]').text(currentSize + textAvailable);
                        if ($('button.coming__soon').length) {
                            $('[data-current-size]').text(currentSize + coming_soon)
                        }
                    } else {
                        $('[data-current-size]').text(currentSize);
                    }
                }

                if(item.option1 === currentColorLang) {
                    var _size = $('[data-option="'+item.option2+'"]');
                    _size.attr('data-variant-id',item.id);
                    if(!item.available) {
                        _size.addClass('disabled');
                        var _available = $('<span/>')
                            .addClass('data-item-available')
                            .text($('button.coming__soon').length ? coming_soon : textAvailable)
                            .appendTo(_size.find('label'));
                    }
                }
                if(item.option2 === currentSize) {

                    var _color = $('[data-option="'+item.option1+'"]');
                    _color.attr('data-variant-id',item.id);
                    if(!item.available) {
                        _color.addClass('disabled');
                    }
                }

                setTimeout(function() {
                    if(item.id == _select.val() && !$('.sc-builder').length) {
                        var _src = item && item.featured_image && item.featured_image.src || product.featured_image;
                        currentImg = '<img alt="PDP Product" src="'+ _src +'" width="38px" height="50px"/>';
                        pdpFormTop();
                    }
                }, 2000);

            });
        };

        var closeModal = function ($elem){
            $(document).on('click touch', function(e){
                e.stopPropagation();
                if ($elem.has(e.target).length === 0) {
                    $elem.removeClass('open');
                }
            });
        };

        $(document).ready(function(){
            var _mobileContainer = $('.product-single__media-group');
            var _btn = $('<button/>')
                .addClass('close-pinch')
                .appendTo(_mobileContainer);
            zoomMedia();
            selectedVariant();
            $(window).on('resize', function() {
                zoomMedia();
            });

            var _selectContainer = $('.select-container');
            if(_selectContainer) {
                var _action = _selectContainer.find('.pos-title-selected');
                var _label = _selectContainer.find('label');
                _action.on('click touch', function (){
                    _selectContainer.toggleClass('open');
                });

                _label.on('click touch', function (){
                    _selectContainer.removeClass('open');
                });
                closeModal(_selectContainer);
            }

            var _sizeChart = $('.block-size-chart span');
            if(_sizeChart) {
                var _cart = $('.size-chart-modal__content');
                _sizeChart.on('click touch', function() {
                    if(_cart){
                        _cart.addClass('open');
                        $('body').addClass('popup-open');
                    }
                });
                $('.bg-section_cart-size, .size-chart__close').on('click touch', function() {
                    _cart.removeClass('open');
                    $('body').removeClass('popup-open');
                });
            }
            var _guideChart = $('.block-size-guide span');
            if(_sizeChart) {
                var _guide = $('.block-size-guide');
                _guideChart.on('click touch', function() {
                    if(_guide){
                        _guide.addClass('guide__open');
                        $('body').addClass('popup-open');
                        $('#PageContainer').css('z-index', '22')
                    }
                });
                $('.guide__backdrop, .guide-chart__close').on('click touch', function() {
                    _guide.removeClass('guide__open');
                    $('body').removeClass('popup-open');
                    $('#PageContainer').css('z-index', 'auto')
                });
            }
            $('.product-single__media-group').removeClass('default');
            var _gift_reggie_buttons = $('.gift-reggie-buttons');
            if(_gift_reggie_buttons){
                _gift_reggie_buttons.appendTo($('.form-buttons_add'));
            }

            var _info = $('.block-info.block-description');
            if(_info) {
                _info.each(function(){
                    var $this = $(this);
                    var _action = $this.find('.block-info_title');
                    _action.on('click touch', function(){
                        $this.toggleClass('open');
                    });
                });
            }

            if(window && window.theme && window.theme.badge_icons) {
                var bages = window.theme.badge_icons;
                var firstTag = null;
                var _badge = $('.badge-tag_product');

                if(bages.length) {
                    product.tags.forEach(function (tag) {
                        var _tag = bages.find(function(bage) {
                            if(tag.includes('badge::')) {
                                return bage.tag === tag;
                            }
                            return false;
                        });
                        if(_tag && !firstTag) {
                            firstTag = _tag;
                        }
                    });
                }
                if(_badge.length) {
                    _badge.empty();
                    if(firstTag ) {
                        var _badgeHtml = $('<div/>')
                            .addClass('badge-tag-product badge--' + firstTag.name.toLowerCase())
                            .text(firstTag.name)
                            .appendTo(_badge);
                    }
                }
            }
        });
    }

    $('.guide-size-wrap>div').on('click', function () {
        $('.guide-size-wrap').addClass('open');
    });

    $('.guide-size-wrap li').on('click', function () {
        var size_val = $(this).text();
        var size_url = $(this).attr('data-url');
        $('.guide-size-wrap>div span').text(size_val);
        $('.guide-imgs img').attr('src', size_url);
        $('.guide-size-wrap.open').removeClass('open');
    });

}(jQuery);

// PLP
!function ($){
    $(document).ready(function(){
        var searchParams = new URLSearchParams(window.location.search);
        var keys = searchParams.keys();
        for(key of keys) {
            var _filter = $('.facet.facet-'+key);
            _filter.addClass('active');
            _filter.find('.facet-values').slideDown();
        }

        if(!window.cffCustomer && (window.location.pathname.indexOf('/apps/giftregistry/wishlist') > -1)){
            var _lg = '';
            if(window.theme.locale === 'fr') _lg = '/' + window.theme.locale;
            var data = {
                attributes: {
                    "guest_add_wishlist": _lg + '/apps/giftregistry/wishlist' + window.location.search
                }
            }
            $.post('/cart/update.json', data);
        }

        if(!window.cffCustomer && (window.location.pathname.indexOf('/account/login') > -1)
            && (decodeURIComponent(window.location.search) === '?return_url=/account')){
            var _url = $('input[name="return_url"]');
            var _redirect = window.spotDefault.options &&
                window.spotDefault.options.cart &&
                window.spotDefault.options.cart.attributes &&
                window.spotDefault.options.cart.attributes.guest_add_wishlist;
            if(_url && _url.length && _redirect) {
                var queryParams = new URLSearchParams(window.location.search);
                queryParams.set("return_url", _redirect);
                history.pushState(null, null, "?"+queryParams.toString());
                $('[name="return_url"]').val(_redirect);
                var data = {
                    attributes: {
                        "guest_add_wishlist": null
                    }
                }
                $.post('/cart/update.json', data);
            }
        }
    });

    $('.plp-open-filters-desktop').on('click', function () {
        $('#Collection').toggleClass('plp-full');
        if ($('#Collection').hasClass('plp-full') && window.theme.locale === 'en' ) {
            $('.plp-open-filters-desktop span').text('Show Filters');
        }
        if ($('#Collection').hasClass('plp-full') && window.theme.locale === 'fr' ) {
            $('.plp-open-filters-desktop span').text('Afficher les filtres');
        }
        if (!$('#Collection').hasClass('plp-full') && window.theme.locale === 'en' ) {
            $('.plp-open-filters-desktop span').text('Hide Filters');
        }
        if (!$('#Collection').hasClass('plp-full') && window.theme.locale === 'fr' ) {
            $('.plp-open-filters-desktop span').text('Cacher les filtres');
        }
    });

    $('.plp-spot-filters-wrap').on('click', '.plp-spot-filters .facet-title', function () {
        if ($(this).parent().hasClass('active')) {
            $(this).parent().removeClass('active');
            $(this).next().slideUp();
        } else {
            $('.plp-spot-filters .facet').removeClass('active');
            $('.plp-spot-filters .facet-values').slideUp();
            $(this).parent().addClass('active');
            $(this).next().slideDown()
        }
    });

    $('body').on('mouseenter', '.product--info-colors button', function () {
        var color_hover = $(this).data('color');
        var div_product = $(this).closest('.product');
        div_product.find('.product--image .img').addClass('hide');
        var s_ = '.product--image .img.' + color_hover;
        div_product.find(s_).removeClass('hide');
        div_product.find(s_).addClass('show');
    });

    $('body').on('mouseleave', '.product--info-colors button', function () {
        var div_product = $(this).closest('.product');
        div_product.find('.product--image .img').removeClass('hide');
        div_product.find('.product--image .img').removeClass('show');
    });

    $('body').on('click', '.product--info-colors button', function () {
        var color_click = $(this).data('color');
        var color_full_click = $(this).data('color-full');
        var o_pos = "data-option"+$(this).data('pos');
        var o_pos_ = "option"+$(this).data('pos');
        var div_product = $(this).closest('.product');

        var string_code = div_product.data('variants-json');
        string_code = string_code.replaceAll('+', '%20');
        var data_json = JSON.parse(decodeURIComponent(string_code));

        div_product.find('.q-form').attr(o_pos, color_full_click);

        var h_ot1 = 'input[name="properties[_option_title'+ $(this).data('pos') +']"]';
        div_product.find(h_ot1).val('Color');
        var h_o1 = 'input[name="properties[_option'+ $(this).data('pos') +']"]';
        div_product.find(h_o1).val(color_full_click);

        if (div_product.find('.q-add-size button').length > 0) {
            div_product.find('.q-add-size button').hide().removeClass('active').removeClass('click');
            div_product.find('.q-add-size button[data-color="'+ color_full_click +'"]').show();
            div_product.find('.q-add-size button').each(function () {
                var div_button = $(this);
                var t_size = $(this).data('size');
                var t_pos = "option"+$(this).data('pos');
                var nr_size = data_json.filter(function(v) {
                    return (v[o_pos_] == color_full_click && v[t_pos] == t_size) })

                if (nr_size.length) {
                    if (nr_size[0].available) {
                        if (nr_size[0].inventory_quantity > 0) {
                            div_button.addClass('active');
                        }
                    }
                }
            });
        }

        div_product.find('.product--info-colors button').removeClass('active');
        div_product.find('.product--info-colors button.swatch-'+color_click).addClass('active');

        div_product.find('.product--image img').removeClass('show_');
        div_product.find('.product--image img.'+color_click).addClass('show_');

        div_product.find('.product--image video').removeClass('show_');
        div_product.find('.product--image video.'+color_click).addClass('show_');

    });


    function submitPLPProduct(form_product, data_json) {
        var op1 = form_product.getAttribute('data-option1');
        var op2 = form_product.getAttribute('data-option2');
        var op3 = form_product.getAttribute('data-option3');
        var find_product = data_json.filter(function(p) {
            if (p.option3) return(p.option1 == op1 && p.option2 == op2 && p.option3 == op3);
            if (p.option2) return(p.option1 == op1 && p.option2 == op2);
            if (p.option1) return(p.option1 == op1 );
        });

        if (find_product.length > 0) {
            form_product.querySelector('input[name="id"]').value = find_product[0].id;

            window.Cart.addItem(theme_.Helpers.serialize(form_product))
              .done(function() {
                  window.Cart.openCart();
              })
              .fail(function() {
                  atc.html(theme.strings.addingFail);
                  console.log("failed to add");
              })
              .always(function() {
                  setTimeout(function() {
                      // Trigger a quantity update to update atc button text
                      $(document).trigger('cart.qty_update');
                  }, 4000);
              });
        }
    }

    $('body').on('click', '.q-add-size button', function () {
        if ($(this).hasClass('active')) {
            var size_click = $(this).data('size');
            var o_pos = "data-option"+$(this).data('pos');
            var div_product = $(this).closest('.product');
            var string_code = div_product.data('variants-json');
            string_code = string_code.replaceAll('+', '%20');
            var data_json = JSON.parse(decodeURIComponent(string_code));

            var h_ot1 = 'input[name="properties[_option_title'+ $(this).data('pos') +']"]';
            div_product.find(h_ot1).val('Size');
            var h_o1 = 'input[name="properties[_option'+ $(this).data('pos') +']"]';
            div_product.find(h_o1).val(size_click);

            div_product.find('.q-form').attr(o_pos, size_click);

            div_product.find('.q-add-size button').removeClass('click');
            //$(this).addClass('click');

            submitPLPProduct(div_product.find('.q-form')[0], data_json);
        }
    })

    $('body').on('click', 'button.q-add', function () {
        var div_product = $(this).closest('.product');
        var string_code = div_product.data('variants-json');
        string_code = string_code.replaceAll('+', '%20');
        var data_json = JSON.parse(decodeURIComponent(string_code));
        $(this).hide();

        var default_pos = "data-option"+div_product.find('.product--info-colors button.active').data('pos');
        var default_pos_ = "option"+div_product.find('.product--info-colors button.active').data('pos');
        var default_color = div_product.find('.product--info-colors button.active').data('color-full');

        var h_ot1 = 'input[name="properties[_option_title'+ div_product.find('.product--info-colors button.active').data('pos') +']"]';
        div_product.find(h_ot1).val('Color');
        var h_o1 = 'input[name="properties[_option'+ div_product.find('.product--info-colors button.active').data('pos') +']"]';
        div_product.find(h_o1).val(default_color);

        div_product.find('.q-form').addClass('active');
        div_product.find('.q-form').attr(default_pos, default_color);
        if (div_product.find('.q-add-size button').length > 0) {
            div_product.find('.q-add-size button').hide().removeClass('active');
            div_product.find('.q-add-size button[data-color="'+ default_color +'"]').show();
            div_product.find('.q-add-size button').each(function () {
                var div_button = $(this);
                var t_size = $(this).data('size');
                var t_pos = "option"+$(this).data('pos');
                var nr_size = data_json.filter(function(v) {
                    return (v[default_pos_] == default_color && v[t_pos] == t_size) });
                if (nr_size.length) {
                   if (nr_size[0].available) {
                       if (nr_size[0].inventory_quantity > 0) {
                           div_button.addClass('active');
                       }
                   }
                }
            });
        }
    })

    $('body').on('mouseleave', '.product', function () {
        var div_product = $(this);
        div_product.find('button.q-add').show();
        div_product.find('form.q-form').removeClass('active');
    })

    $('.plp-open-filters-mobile').on('click', function () {
        if($( document ).width() < 1025) {
            $(this).toggleClass('open');
            $(this).next().slideToggle()
        }
    });

    $('.collection__menu-open').on('click', function () {
        $(this).toggleClass('open');
        $('.collection__menu').toggleClass('open');
    });

    $('.plp-open-filters-mobile').on('click', function () {
        $('.plp-products-body .plp-section-left').addClass('show');
    });
    $('.plp-fm-header-close, .plp-fm-footer button').on('click', function () {
        $('.plp-products-body .plp-section-left').removeClass('show');
    });

}(jQuery);
// End PLP


/**
 * Cart Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Cart template.
 *
 * @namespace cart
 */

(function($) {

    var settings = {
        bagels: true,
        moneyFormat: window.theme.moneyFormat,
        freeShippingThreshold: window.theme.freeShippingThreshold,
        shippingRate: window.theme.shippingRate
    };

    var cart = new function() {
        function toMoneyy(price, options) {
            if (price !== 0 && !price) {
                console.error('Utilities.toMoney: Invalid price passed.', price);
                return null;
            }

            var price = new Number(price);
            var options = options || {};
            var currency_options = {
                style: 'currency'
            }

            // Assign defaults if no options are passed.
            options.cents = options.cents || false;
            options.locale = options.locale || 'en';
            currency_options.currency = options.currency || 'CAD';

            // Convert to dollars if the amount passed is in cents
            if (options.cents) {
                price = price / 100;
            }

            // Check if there are decimals and no zero option
            if (options.no_zeros && !price.toString().split('.')[1]) {
                currency_options.minimumSignificantDigits = 2;
            }

            return price.toLocaleString(options.locale, currency_options);
        };

        function Cart(settings) {

            this.defaults = {
                selectors : {
                    cartOpen: ".js-cart-open",
                    cartClose: ".js-cart-close",
                    cartClose2: ".js-cart-close2",
                    cartItems: ".js-cart-items",
                    cartItemsCount: ".js-cart-item-count",
                    cartItemDelete: ".js-item-delete",
                    cartSave: ".js-cart-save",
                    cartTotal: ".js-cart-total",
                    cartFreeShippingDifference: ".js-cart-shipping-difference",
                    cartItem: ".js-cart-item",
                    cartItemMinus: ".js-cart-minus",
                    cartItemQty: ".js-cart-qty",
                    cartItemPlus: ".js-cart-plus",
                    cartShippingRate: ".js-cart-shipping",
                    cartStyleParent: "body", // Container to hold action styling
                    cartFlyout: "#cart-flyout"
                },
                classes : {
                    cartOpened: "cart__open",
                    itemDisabled: "disabled",
                    freeShipping: "cart__free-shipping",
                    cartEmpty: "cart__empty"
                },
                itemData: {
                    id: "item-id"
                },
                moneyFormat: "${{amount}}",
                freeShippingThreshold: 5000,
                shippingRate: 500
            }

            this.options = $.extend(this.defaults, settings);

            this.$cartStyleParent = $(this.options.selectors.cartStyleParent);
            this.$cartItemsCount = $(this.options.selectors.cartItemsCount);
            this.$cartItems = $(this.options.selectors.cartItems);
            this.$cartFlyout = $(this.options.selectors.cartFlyout);
            this.itemQuantities = {};

            this.initDynamicSelectors();

            this.init();
        }

        Cart.prototype.initDynamicSelectors = function() {
            this.$cartTotal = $(this.options.selectors.cartTotal);
            this.$cartSave = $(this.options.selectors.cartSave);
            this.$cartFreeShippingDifference = $(this.options.selectors.cartFreeShippingDifference);
            if (this.options.shippingRate === 0) {
                $('.cart__price__shipping__').hide();
            } else {
                this.$cartShippingRate = $(this.options.selectors.cartShippingRate);
                this.$cartShippingRate.html(toMoneyy(this.options.shippingRate, {locale: window.theme.locale, cents: true, currency: 'CAD' }));
            }
        };

        Cart.prototype.init = function() {
            var _ = this;
            $(_.options.selectors.cartOpen).on('click', function(event) {
                event.preventDefault();
                _.openCart();
            });

            $(_.options.selectors.cartClose).on('click', function(event) {
                event.preventDefault();
                $('span[data-cart-count]').text($('span.cart-nr-products').text());
                $('span[data-cart-count-mobile]').text($('span.cart-nr-products').text());
                _.$cartStyleParent.removeClass(_.options.classes.cartOpened);
            });
            $('.cart__flyout').on('click', _.options.selectors.cartClose2, function(event) {
                event.preventDefault();
                $('span[data-cart-count]').text($('span.cart-nr-products').text());
                $('span[data-cart-count-mobile]').text($('span.cart-nr-products').text());
                _.$cartStyleParent.removeClass(_.options.classes.cartOpened);
            });

            _.updateCartInfo();
            //_.refreshFlyout(document._shopify_cart.cart);
        };

        Cart.prototype.refreshFlyout = function(data, outerpromise) {
            var _ = this;
            var promise = outerpromise || $.Deferred();

            if (typeof(data) !== 'object') {
                data = JSON.parse(data);
            }

            data.items = data.items || data.line_items;

            $.get(window.Shopify.routes.root+'cart?view=flyout', function(resp) {
                _.$cartFlyout.html(resp);

                _.initDynamicSelectors();
                _.setSubTotal(data);

                _.itemQuantities = {};

                $('.cart__item').each(function() {
                    var element = {
                        id: $(this).data('item-id'),
                        quantity: $(this).find('.js-cart-qty').val()
                    }
                    _.initItem($(this), element, false);
                });

                $(document).trigger('cart.qty_update');

                promise.resolve();
            }).fail(function () {
                promise.reject();
            });

            // Update empty cart state
            if(data.items.length > 0) {
                _.$cartStyleParent.removeClass(_.options.classes.cartEmpty);
            } else {
                _.$cartStyleParent.addClass(_.options.classes.cartEmpty);
            }

            var item_ids = document._shopify_cart.cart.line_items.map(function(item) {
                return item.product_id;
            });

            $('[data-cart-badge]').find('.badge-cart').remove();
            $.each(item_ids, function(index, id) {
                $('[data-cart-badge="' + id + '"]').append('<img class="product-badge badge-cart" src="' + window.theme.badges.cart + '">');
            });

            _.setCartCount(data);

            return promise;
        };

        Cart.prototype.updateCartInfo = function() {
            var promise = document._shopify_cart._refresh();
            document._shopify_cart._run_campaigns();
            return promise;
        };

        Cart.prototype.setCartCount = function(data) {
            var _  = this;
            _.$cartItemsCount.html(data.item_count);
        };

        Cart.prototype.setSubTotal = function(data) {
            var _  = this;

            var shippingDifference = _.options.freeShippingThreshold - data.total_price;
            var shippingCost = _.options.shippingRate;

            _.$cartFreeShippingDifference.html(toMoneyy(shippingDifference, {locale: window.theme.locale, cents: true, currency: 'CAD' }));

            if(shippingDifference > 0) {
                _.$cartStyleParent.removeClass(_.options.classes.freeShipping);
            } else {
                _.$cartStyleParent.addClass(_.options.classes.freeShipping);
                shippingCost = 0;
            }

            var _total_price = data.total_price + shippingCost;
            _.$cartSave.html(toMoneyy(data.total_discount , {locale: window.theme.locale, cents: true }));
            _.$cartTotal.html(toMoneyy(_total_price, {locale: window.theme.locale, cents: true }));
            _.$cartTotal.attr('data-total', _total_price);
            window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.variants.map(pv => {pv.price = _total_price});
            $("#klarna--placement-minicart").attr("data-purchase-amount", _total_price);
        };

        Cart.prototype.setItemQty = function($qty, qty) {
            $qty.val(qty);
        };

        Cart.prototype.initItem = function(item, itemData, prepend) {
            var _ = this;

            var $item = $(item);
            var id = itemData.id;

            $(_.options.selectors.cartItemDelete, $item).on("click", function(event) {
                _.deleteItem(id, $item);
            });

            var $qty = $(_.options.selectors.cartItemQty, $item);

            _.setItemQty($qty, itemData.quantity);

            var min = parseInt($qty.attr('min'));

            $(_.options.selectors.cartItemPlus, $item).on("click", function(event) {
                _.updateQuantity(parseInt($qty.val()) + 1, id, $qty, $item, min);
            });

            $(_.options.selectors.cartItemMinus, $item).on("click", function(event) {
                _.updateQuantity(parseInt($qty.val()) - 1, id, $qty, $item, min);
            });

            $qty.on("blur", function(event) {
                _.updateQuantity(parseInt($qty.val()), id, $qty, $item, min);
            });

            var idValue = itemData.id.split(":")[0];

            if(_.itemQuantities[idValue]) {
                _.itemQuantities[idValue] += parseInt(itemData.quantity);
            } else {
                _.itemQuantities[idValue] = parseInt(itemData.quantity);
            }
        };

        Cart.prototype.addItem = function(formData) {
            var _ = this;
            var result = $.Deferred();

            $.post("/cart/add.js", formData, undefined, "json")
              .then(function(element) {

                  // Call cart to update pricing info
                  return $.post("/cart.json");
              })
              .then(function(data) {
                  return _.updateCartInfo(data);
              })
              .done(function() {
                  result.resolve();
              })
              .fail(function(error) {
                  result.reject(error);
              });

            return result;
        };

        Cart.prototype.deleteItem = function(itemId, $item) {
            var _ = this;

            $item.addClass(_.options.classes.itemDisabled);

            $.post("/cart/change.js", { id: itemId, quantity: 0 }, undefined, "json")
              .done(function(data) {
                  $item.slideUp(function() {
                      _.updateCartInfo(data);
                  });
              })
              .fail(function() {
                  $item.removeClass(_.options.classes.itemDisabled);
              });
        };

        Cart.prototype.updateQuantity = function(qty, itemId, $qty, $item, min) {
            var _ = this;

            if(min !== NaN) {
                qty = Math.max(qty, min);
            }

            $item.addClass(_.options.classes.itemDisabled);

            $.post("/cart/change.js", { id: itemId, quantity: qty }, undefined, "json")
              .done(function(data) {

                  var items = data.items.filter(function(item) {
                      return item.id === itemId;
                  });

                  if(items.length > 0) {
                      _.itemQuantities[item[0].id] = item[0].quantity;
                      $(document).trigger('cart.qty_update');
                      _.setItemQty($qty, items[0].quantity);
                  }

                  _.updateCartInfo(data);
              })
              .always(function() {
                  $item.removeClass(_.options.classes.itemDisabled);
              });
        };

        Cart.prototype.formatMoney = function(value) {
            var _ = this;
            return _.options.moneyFormat.replace("{{amount}}", (value / 100).toFixed(2));
        };

        Cart.prototype.openCart = function () {
            var _ = this;
            _.$cartStyleParent.addClass(_.options.classes.cartOpened);
        };

        return Cart;
    };

    // Initialize the cart on load
    $(function() {
        window.Cart = window.Cart || new cart(settings);
    });
})(jQuery);

// end adddddddd
// start banner slick text
var item_length = $('.pbTxt-slider > div').length - 1;
var slider = $('.pbTxt-slider').slick({
    autoplay: true,
    autoplaySpeed: 5000,
    dots: false,
    infinite: false,
    speed: 1000,
    fade: true,
    slide: 'div',
    cssEase: 'linear',
    onAfterChange: function(){
        if( item_length == slider.slickCurrentSlide() ){
            slider.slickSetOption("autoplay",false,false)
        };
    }
});

// Form submission handling for the gift card balance checker page

$(function () {
    var $submitBtn = $("#card-balance-form-submit");
    var $reloadBtn = $("#card-balance-form-reload");
    var $cardForm = $('#card-balance-form');
    var $responseContainer = $('#gift-card-message');
    var submitFormWithRecaptcha = function () {
        var VERIFY_CARD_URL = '/apps/api/v1/check_gift_card_balance';

        $responseContainer.hide().empty().removeClass('text-danger');

        $.post({
            url: VERIFY_CARD_URL,
            data: $('#card-balance-form').serialize(),
            dataType: 'json'
        })
            .done(function(r) {
                $responseContainer.slideDown().html(r.message);
            })
            .fail(function(r) {
                if(r.responseJSON) {
                    var message = giftCardMessageTranslation (r.responseJSON.message.split("-")[0].replace('gift_card_code', 'card number').replace('pin_number', 'pin number'));
                    $responseContainer.slideDown().html(message).addClass('text-danger');
                } else if (r.status === 404) {
                    $responseContainer.slideDown().html("Error connecting to server").addClass('text-danger');
                }
            })
            .always(function() {
                $submitBtn.hide();
                $reloadBtn.show();
            });
    }

    $cardForm.submit(function(e) {
        e.preventDefault();

        $submitBtn.prop('disabled', true);

        grecaptcha.reset();
        grecaptcha.execute();

        setTimeout(function () {
            submitFormWithRecaptcha()
        }, 2000)

    });

    $reloadBtn.click(function(e) {
        $submitBtn.show();
        $reloadBtn.hide();
        $submitBtn.prop('disabled', false);
        $responseContainer.hide().empty().removeClass('text-danger');
        $('#giftcartInput').val('')
        $('#giftcartpinInput').val('')
    });

    $submitBtn.prop('disabled', false);
});

// Generate the captcha on the gift card page


$(function () {
    if (theme && theme.template && theme.template.suffix == 'gift-card-checker') {
        var actCallback2 = function actCallback() {
            $('.scgh-btn-submit').attr('disabled', false);
        };

        var expCallback2 = function expCallback() {
            $('.scgh-btn-submit').attr('disabled', true);
        };

        window.addEventListener('load', function (event) {
            $('.scgh-btn-submit').attr('disabled', true);
            $('.scgh-checker .scgh-checker__form button.scgh-btn-submit').css('cssText', 'display: flex !important');
            $('.scgh-btn-submit').before('<div id="re-captcha2" data-callback="enableBtn2"></div>');

            function enableBtn2() {
                $('.scgh-btn-submit').attr('disabled', false);
            }
        }, false);

        setTimeout(function () {
            window.grecaptcha.render(document.getElementById('re-captcha2'), {
                'sitekey': '6LdDlVImAAAAAIyje1QAoSyagBNrPLE1oYO0MbcZ',
                // Sitekey, Retrieving from reCaptcha
                'theme': 'light',
                'callback': actCallback2,
                'expired-callback': expCallback2
            });
        }, 2000);

    }
});

function giftCardMessageTranslation(en_message) {
    var fr_message = "";

    if ($("html").attr("lang") == "fr") {
        if (en_message == "Missing parameters: card number") {
            return fr_message = "Paramètre manquant: numéro de carte";
        } else if (en_message == "Missing parameters: pin number") {
            return fr_message = "Paramètre manquant: numéro de nip";
        } else if (en_message == "Missing parameters: card number, pin number") {
            return fr_message = "Paramètres manquants: numéro de carte et numéro de nip";
        } else if (en_message == "Invalid Card #") {
            return fr_message = "Numéro de Carte Invalide";
        } else if (en_message = "Check the entered information") {
            return fr_message = "Vérifier les informations entrées";
        } else {
            return "Vérifier les informations entrées";
        }
    } else {
        return "Check the entered information";
    }
}

(function ($){
    var today = new Date();
    var expiry = new Date(today.getTime() + 30 * 24 * 3600 * 1000); // plus 30 days

    function setCookie(name, id) {
        var element = document.getElementById(id);
        var elementValue = escape(element.value);

        document.cookie = name + "=" + elementValue + "; path=/; expires=" + expiry.toGMTString();
    }

    function getCookie(name) {
        var re = new RegExp(name + "=([^;]+)");
        var value = re.exec(document.cookie);
        return (value != null) ? unescape(value[1]) : null;
    }

    $('a.newsletter__submit').on('click', function () {
        setCookie('email','ContactFooter-email');
    });

    setTimeout(function() {
        if ($('input.needsclick[type="email"]').length) {
            $('input.needsclick[type="email"]').val(getCookie('email'));
        }
    }, 1000);

})(jQuery);

//contact us
$(function () {
    $('.contact-item-live').on('click', function() {
        $(this).toggleClass("open");
    });
    $('.contact-item-call').on('click', function() {
        $(this).toggleClass("open");
    });
    $('.contact-item-email').on('click', function() {
        $(this).toggleClass("open");
    });
});

// gift-card footer
$(function () {
    var text_en = '<p><u>Terms and conditions for e-gift cards:</u></p>' +
      '<p>E-gift cards are redeemable for <strong>online purchases only</strong> and can be used to purchase both Suzy Shier and Le Chateau merchandise.</p>' +
      '<p>E-gift cards are redeemable in CAD only and cannot be applied to purchases made with Affirm.</p>' +
      '<p>You can use your Suzy Shier / Le Chateau e-gift card to make full or partial payments. Coupons and discounts, including Prestige Card discounts, cannot be applied to e-gift card purchases. If other merchandise is included in your order, the coupon value will apply only to those items.</p>' +
      '<p>Sales tax will not be applied to gift card purchase. If other merchandise is included in your order, tax will apply only to those items.</p>' +
      '<p>All e-gift card purchases are final sale, non-refundable, non-exchangeable and cannot be redeemed for cash.</p>' +
      '<p>Suzy’s Inc. is not responsible for lost or stolen gift cards.</p>';

    var text_fr = '<p><u>Conditions générales des e-cartes cadeaux :</u></p>' +
      '<p>Les cartes-cadeaux électroniques ne sont échangeables que pour les <strong>achats en ligne</strong> et peuvent être utilisées pour acheter des produits Suzy Shier et Le Chateau.</p>' +
      '<p>Les cartes-cadeaux électroniques sont échangeables en CAD uniquement et ne peuvent pas être appliquées aux achats effectués avec Affirm.</p>' +
      '<p>Vous pouvez utiliser votre e-carte cadeau Suzy Shier / Le Château pour effectuer des paiements complets ou partiels.</p>' +
      '<p>Les coupons et les remises, y compris les remises de la carte Prestige, ne peuvent pas être appliquées aux achats de cartes-cadeaux électroniques. Si d\'autres marchandises sont incluses dans votre commande, la valeur du coupon ne s\'appliquera qu\'à ces articles.</p>' +
      '<p>La taxe de vente ne s\'appliquera pas à l\'achat d\'une carte-cadeau. Si d\'autres marchandises sont incluses dans votre commande, la taxe s\'appliquera uniquement à ces articles.</p>' +
      '<p>Tous les achats de cartes-cadeaux électroniques sont des ventes finales, non remboursables, non échangeables et ne peuvent être échangés contre de l\'argent.</p>' +
      '<p>Suzy\'s Inc. n\'est pas responsable des cartes-cadeaux perdues ou volées.</p>'

    setTimeout(function() {
        $('.sc-builder .sc-footer').html(window.Shopify.locale === 'en' ? text_en : text_fr);
    }, 3000);

    setInterval(function() {
        if ($('.sc-card-type .sc-card-tabs .sc-card-tab--active').length &&
          $('.sc-card-type .sc-card-tabs .sc-card-tab--active').text() !== $('.sc-builder>h1').text()) {
            $('.sc-builder>h1').text($('.sc-card-type .sc-card-tabs .sc-card-tab--active').text());
          }
    }, 500);

});

$(function () {
    $('ul.site-footer__linklist>li>button').on('click', function () {
        $(this).parent().toggleClass('open');
    })
});

(function ($){
    var _form_newsletter = $('.account__form-newsletter');
    if(_form_newsletter.length){
        _form_newsletter.each(function(){
            var _this = $(this);
            var email = _this.find('[name="customer[email]"');
            var _submit = _this.find('.btn--account[type="submit"]');
            var _checkbox = _this.find('[name="checkbox_label_login"]');
            _submit.on('click', function(e){
                if(_checkbox.is(':checked')){
                    localStorage.setItem('klaviyo-customer-account_email', email.val());
                }
            });
        });
    }
})(jQuery);

